import { useContext, useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { withTranslation } from 'react-i18next';
import { AiOutlineCalculator } from 'react-icons/ai';
import { BiDownArrow, BiInfoCircle, BiPlusCircle, BiTrash, BiUpArrow } from 'react-icons/bi';
import { toast } from 'react-toastify';
import { RegulationContext } from '../../../App';
import { CategoryList, DataViewCategoryType, EquationBuilderFor, LS_COMPANY_INFO_KEY, LS_REGULATION_SELECTED_KEY, MonthsExt, StandardCategory } from '../../../core/constants/common';
import CompanyService from '../../../core/service/CompanyService';
import StandardService from '../../../core/service/StandardService';
import EquationBuilder from '../../Common/Standards/EquationBuilder/EquationBuilder';
import '../GHGEmissions/GHGEmissionDataManagement/GHGEmissionDataManagement.scss';
import {
  ESG_ANALYTICS_REPORTING_PERIOD_END_YEAR,
  ESG_ANALYTICS_REPORTING_PERIOD_START_YEAR,
  getCompareYearPeriodRangeLabel,
} from './esgAnalyticsPeriodRange';

/** Display string for metric/datapoint objects in Typeahead */
const metricDatapointTypeaheadLabel = (item) => {
  if (!item) return '';
  const code = item.code ?? '';
  const name = item.name ?? '';
  if (code && name) return `${code} - ${name}`;
  return name || code;
};

/** Level 1 config rows: `sortOrder` is 1-based, in display order. */
const withSortOrders = (rows) => rows.map((row, i) => ({ ...row, sortOrder: i + 1 }));

const ESGUnitWiseConfigForm = ({ t, onFilterChange, filter: parentFilter, onApplyFilters, onConfigRowsChange, initialConfigRows, editMode = false,  onSave, title: parentTitle, onTitleChange, simplifiedEditForm = false }) => {
  const { glbRegulation } = useContext(RegulationContext);
  const glbRegulationId = glbRegulation?.id ?? localStorage.getItem(LS_REGULATION_SELECTED_KEY) ?? "";

  const [filter, setFilter] = useState({
    standardId: glbRegulationId,
    categoryType: DataViewCategoryType.ENVIRONMENTAL,
    topicIds: [],
    parentEntities: [],
    childEntities: [],
    periodType: 'YEARLY',
    period: null,
    selectedRepPeriod: [],
  });

  const [topics, setTopics] = useState([]);
  const [parentEntities, setParentEntities] = useState([]);
  const [childEntities, setChildEntities] = useState([]);
  const [period, setPeriod] = useState([]);
  const [selectedData, setSelectedData] = useState([]);
  const [enterpriseStructure, setEnterpriseStructure] = useState([]); // Hierarchical enterprise structure for view mode
  const [expandedEntities, setExpandedEntities] = useState({}); // Track expanded/collapsed state

  // New form state for config rows
  const [allMetrics, setAllMetrics] = useState([]); // All metrics from API
  const [allDatapoints, setAllDatapoints] = useState([]); // All datapoints from API
  const [configRows, setConfigRows] = useState(() => {
    if (initialConfigRows && Array.isArray(initialConfigRows) && initialConfigRows.length > 0) {
      const rows = [...initialConfigRows];
      rows.sort((a, b) => {
        const sa = Number(a.sortOrder);
        const sb = Number(b.sortOrder);
        const ha = Number.isFinite(sa);
        const hb = Number.isFinite(sb);
        if (ha && hb) return sa - sb;
        if (ha) return -1;
        if (hb) return 1;
        return 0;
      });
      return withSortOrders(rows);
    }
    return [];
  }); // Array of config objects
  const [formName, setFormName] = useState('');
  const [title, setTitle] = useState(parentTitle || ''); // Title field for the configuration

  // Level 1 - Separate metric and datapoint selections
  const [selectedLevel1Metric, setSelectedLevel1Metric] = useState(null);
  const [selectedLevel1Datapoint, setSelectedLevel1Datapoint] = useState(null);
  const [formLevel1Equation, setFormLevel1Equation] = useState(null);
  const [formLevel1VerticalCalcEnabled, setFormLevel1VerticalCalcEnabled] = useState(false);
  const [level1SelectionType, setLevel1SelectionType] = useState('metric'); // 'metric', 'datapoint', or 'equation'
  const [showPercentage, setShowPercentage] = useState(true); // Show percentage column in second card

  // Level 2 - Row-based structure with label and metric/datapoint pairs
  const [level2Rows, setLevel2Rows] = useState([]);

  // Equation state - track which equation builder is open
  const [openEquationIndex, setOpenEquationIndex] = useState({
    configIndex: null,
    rowId: null,
    level: null // 'level1' or 'level2' or 'formLevel1' or 'formLevel2'
  });

  // When true, show required validation (red borders) on form fields; used when Add Widgets is clicked
  const [showValidationState, setShowValidationState] = useState(false);

  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showTopicDropdown, setShowTopicDropdown] = useState(false);
  const [showParentEntityDropdown, setShowParentEntityDropdown] = useState(false);
  const [showChildEntityDropdown, setShowChildEntityDropdown] = useState(false);
  const [showPeriodTypeDropdown, setShowPeriodTypeDropdown] = useState(false);
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);

  // Delete config row (Level 1 data) confirmation
  const [showDeleteConfigRowModal, setShowDeleteConfigRowModal] = useState(false);
  const [deleteConfigRowIndex, setDeleteConfigRowIndex] = useState(null);

  // Delete Level 2 row confirmation
  const [showDeleteLevel2RowModal, setShowDeleteLevel2RowModal] = useState(false);
  const [deleteLevel2ConfigIndex, setDeleteLevel2ConfigIndex] = useState(null);
  const [deleteLevel2RowId, setDeleteLevel2RowId] = useState(null);

  // Scroll to newly added Level 1 config row in Edit when clicking "Add New Level1 Data"
  const lastConfigRowRef = useRef(null);
  const scrollToLastConfigRowRef = useRef(false);

  const optionsMonth = MonthsExt({ t });
  const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];

  const parseCompanyInfoFromStorage = () => {
    try {
      return JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
    } catch {
      return {};
    }
  };

  useEffect(() => {
    if (!glbRegulationId) return;
    getTopics();
    getEnterprisesByLevel(1);
    getEnterprisesByLevel(2);
    setFilter(prev => ({ ...prev, standardId: glbRegulationId }));
  }, [glbRegulationId, filter.categoryType]);

  // Fetch enterprise structure for view mode
  useEffect(() => {
    if (!editMode) {
      getEnterpriseStructureMin();
    }
  }, [editMode]);


  useEffect(() => {
    if (filter.topicIds?.length == 0) return;

    loadAllMetricsAndDatapoints(filter.topicIds);
  }, [glbRegulationId, filter.topicIds]);

  useEffect(() => {
    if (onConfigRowsChange) {
      onConfigRowsChange(configRows);
    }
  }, [configRows, onConfigRowsChange]);

  // Sync local filter state with parent filter prop
  useEffect(() => {
    if (parentFilter) {
      setFilter(prev => ({
        ...prev,
        ...parentFilter
      }));
    }
  }, [parentFilter]);

  // Sync title with parentTitle prop (only when parentTitle actually changes)
  useEffect(() => {
    if (parentTitle !== undefined && parentTitle !== title) {
      setTitle(parentTitle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentTitle]); // Intentionally exclude title to avoid loops

  // Notify parent of title changes (only when title is changed by user, not by parent sync)
  useEffect(() => {
    if (onTitleChange && title !== undefined && title !== parentTitle) {
      onTitleChange(title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]); // Intentionally exclude onTitleChange and parentTitle to avoid loops

  useEffect(() => {
    setPeriodItems('onload', filter.periodType);
  }, [filter.periodType]);

  // Populate selectedData from selectedRepPeriod when filter is loaded
  useEffect(() => {
    if (filter.selectedRepPeriod && Array.isArray(filter.selectedRepPeriod) && filter.selectedRepPeriod.length > 0 && period.length > 0) {
      const matchedItems = filter.selectedRepPeriod
        .map(savedItem => {
          const matched = period.find(p => {
            if (filter.periodType === 'YEARLY') {
              return p.value === savedItem.value || p.value === parseInt(savedItem.value);
            } else {
              const categoryMatch = p.category === savedItem.category ||
                p.category === parseInt(savedItem.category) ||
                String(p.category) === String(savedItem.category);
              const valueMatch = p.value === savedItem.value ||
                String(p.value) === String(savedItem.value);
              return categoryMatch && valueMatch;
            }
          });
          return matched || null;
        })
        .filter(item => item !== null);

      if (matchedItems.length > 0) {
        setSelectedData(matchedItems);
      }
    } else if (filter.selectedRepPeriod && Array.isArray(filter.selectedRepPeriod) && filter.selectedRepPeriod.length === 0) {
      setSelectedData([]);
    }
  }, [filter.selectedRepPeriod, period, filter.periodType]);

  const setPeriodItems = (type, value) => {
    const yearOptions = [];
    if (value === 'YEARLY') {
      let count = 0;
      for (let i = ESG_ANALYTICS_REPORTING_PERIOD_END_YEAR; i >= ESG_ANALYTICS_REPORTING_PERIOD_START_YEAR; i--) {
        yearOptions.push({
          id: count,
          value: i,
          displayValue: i,
          category: 'Years'
        });
        count++;
      }
      setPeriod(yearOptions);
    } else if (value === 'QUARTERLY') {
      let count = 0;
      for (let i = ESG_ANALYTICS_REPORTING_PERIOD_END_YEAR; i >= ESG_ANALYTICS_REPORTING_PERIOD_START_YEAR; i--) {
        for (let j = 0; j < quarters.length; j++) {
          yearOptions.push({
            category: i,
            value: quarters[j],
            displayValue: i + ' - ' + quarters[j],
            id: count,
          });
          count++;
        }
      }
      setPeriod(yearOptions);
    } else if (value === 'MONTHLY') {
      let count = 0;
      for (let i = ESG_ANALYTICS_REPORTING_PERIOD_END_YEAR; i >= ESG_ANALYTICS_REPORTING_PERIOD_START_YEAR; i--) {
        for (let j = 0; j < optionsMonth.length; j++) {
          yearOptions.push({
            category: i,
            value: optionsMonth[j].value,
            displayValue: i + ' - ' + optionsMonth[j].label,
            id: count,
          });
          count++;
        }
      }
      setPeriod(yearOptions);
    } else {
      setPeriod([]);
    }
    if (type === 'direct') {
      setSelectedData([]);
    }
  };

  const onSelectDeSelectPeriod = (periodItem) => {
    let existData = [...selectedData];
    const index = existData.findIndex(item => item.id === periodItem.id);
    if (index >= 0) {
      existData.splice(index, 1);
    } else {
      existData.push(periodItem);
    }
    setSelectedData(existData);
    preparePeriod(existData);
  };

  const preparePeriod = (data) => {
    let newPeriod = null;
    if (filter.periodType === 'YEARLY') {
      newPeriod = Object.fromEntries(data.map((t) => [t.value, []]));
    } else if (filter.periodType === 'QUARTERLY') {
      const result = Object.groupBy(data, ({ category }) => category);
      for (const year in result) {
        let values = data.filter(element => element['category'] == year);
        result[year] = [];
        values.forEach(data => {
          result[year].push(data['value']);
        });
      }
      newPeriod = result;
    } else {
      const result = Object.groupBy(data, ({ category }) => category);
      for (const year in result) {
        let values = data.filter(element => element['category'] == year);
        result[year] = [];
        values.forEach(data => {
          result[year].push(data['value']);
        });
      }
      newPeriod = result;
    }
    const newFilter = {
      ...filter,
      period: newPeriod,
      selectedRepPeriod: data,
    };
    setFilter(newFilter);
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  const getMonthlyPeriodItemsForYear = (yearCategory) =>
    period.filter((item) => String(item.category) === String(yearCategory));

  const areAllMonthsSelectedForYear = (yearCategory) => {
    const items = getMonthlyPeriodItemsForYear(yearCategory);
    if (!items.length) return false;
    return items.every((p) => isPeriodSelected(p));
  };

  const toggleYearMonthsSelection = (yearCategory) => {
    if (filter.periodType !== 'MONTHLY') return;
    const items = getMonthlyPeriodItemsForYear(yearCategory);
    if (!items.length) return;
    const allSelected = areAllMonthsSelectedForYear(yearCategory);
    let existData = [...selectedData];
    if (allSelected) {
      const ids = new Set(items.map((i) => i.id));
      existData = existData.filter((item) => !ids.has(item.id));
    } else {
      const existingIds = new Set(existData.map((i) => i.id));
      items.forEach((p) => {
        if (!existingIds.has(p.id)) {
          existData.push(p);
          existingIds.add(p.id);
        }
      });
    }
    setSelectedData(existData);
    preparePeriod(existData);
  };

  const getTopics = () => {
    StandardService.getTopicsByStandard(glbRegulationId, false, StandardCategory.QUANTITATIVE, filter.categoryType)
      .then(res => {
        if (res.status !== 200) return;
        setTopics(res.data);
      }).catch(err => { });
  };

  const getEnterprisesByLevel = (level) => {
    CompanyService.getEnterprisesByLevel(level).then(res => {
      if (res.status !== 200) return;
      const entities = res.data.sort((a, b) => a.sortOrder - b.sortOrder);
      if (level === 1) {
        setParentEntities(entities);
      } else if (level === 2) {
        setChildEntities(entities);
      }
    }).catch(err => { });
  };

  const getEnterpriseStructureMin = () => {
    CompanyService.getEnterpriseStructureMin().then(res => {
      if (res.status !== 200) return;
      const data = res.data?.data || res.data || [];
      setEnterpriseStructure(data);

      // Collapse all root-level entities by default
      const collapsedState = {};
      data.forEach(entity => {
        if (entity.id) {
          collapsedState[entity.id] = false;
        }
      });
      setExpandedEntities(collapsedState);
    }).catch(err => {
    });
  };

  const loadAllMetricsAndDatapoints = (tIds) => {
    if (!glbRegulationId) return;
    StandardService.getAllMetricsAndDatapoints(glbRegulationId, null, tIds)
      .then(res => {
        if (res.status !== 200) return;

        // Try different possible response structures
        const responseData = res.data?.data || res.data?.body || res.data;
        const metrics = responseData?.metrics || [];
        const datapoints = responseData?.datapoints || responseData?.dataPoints || [];

        setAllMetrics(metrics);
        setAllDatapoints(datapoints);
      })
      .catch(err => {
      });
  };

  const handleTopicChange = (topicId, checked) => {
    let updatedTopicIds = [...filter.topicIds];
    if (checked) {
      if (!updatedTopicIds.includes(topicId)) {
        updatedTopicIds.push(topicId);
      }
    } else {
      updatedTopicIds = updatedTopicIds.filter(id => id !== topicId);
    }
    const newFilter = {
      ...filter,
      topicIds: updatedTopicIds,
    };
    setFilter(newFilter);
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  const handleTopicSelectAll = () => {
    const allTopicIds = topics.map(topic => topic.id);
    const newFilter = {
      ...filter,
      topicIds: allTopicIds,
    };
    setFilter(newFilter);
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  const handleTopicUnselectAll = () => {
    const newFilter = {
      ...filter,
      topicIds: [],
    };
    setFilter(newFilter);
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  const isTopicSelected = (topicId) => {
    return filter.topicIds.includes(topicId);
  };

  const getSelectedTopicsCount = () => {
    return filter.topicIds.length;
  };

  const areAllTopicsSelected = () => {
    return topics.length > 0 && filter.topicIds.length === topics.length;
  };

  const areNoTopicsSelected = () => {
    return filter.topicIds.length === 0;
  };

  const cloneLevel2RowForConfig = (row) => ({
    id: row.id,
    label: row.label,
    metric: row.metric,
    datapoint: row.datapoint,
    useEquation: row.useEquation,
    equation: row.equation,
    drillDownEnabled: row.drillDownEnabled,
    drillDownType: row.drillDownType,
    level2SelectionType: row.level2SelectionType
  });

  const handleLevel1MetricSelect = (metric) => {
    setSelectedLevel1Metric(metric);
    setSelectedLevel1Datapoint(null);
  };

  const handleLevel1DatapointSelect = (datapoint) => {
    setSelectedLevel1Datapoint(datapoint);
    setSelectedLevel1Metric(null);
  };

  const handleAddLevel2Row = () => {
    const newRow = {
      id: Date.now(),
      label: '',
      metric: null,
      datapoint: null,
      useEquation: false,
      equation: null,
      drillDownEnabled: false,
      drillDownType: 'equation' // 'equation' or 'metric'
    };
    setLevel2Rows([...level2Rows, newRow]);
  };

  const handleRemoveLevel2Row = (rowId) => {
    setLevel2Rows(level2Rows.filter(row => row.id !== rowId));
  };

  const handleLevel2RowLabelChange = (rowId, label) => {
    const updatedRows = level2Rows.map(row =>
      row.id === rowId ? { ...row, label } : row
    );
    setLevel2Rows(updatedRows);
  };

  const handleLevel2RowMetricSelect = (rowId, metric) => {
    const updatedRows = level2Rows.map(row => {
      if (row.id !== rowId) return row;
      if (metric) {
        return { ...row, metric, datapoint: null, level2SelectionType: 'metric' };
      }
      return { ...row, metric: null };
    });
    setLevel2Rows(updatedRows);
  };

  const handleLevel2RowDatapointSelect = (rowId, datapoint) => {
    const updatedRows = level2Rows.map(row => {
      if (row.id !== rowId) return row;
      if (datapoint) {
        return { ...row, datapoint, metric: null, level2SelectionType: 'datapoint' };
      }
      return { ...row, datapoint: null };
    });
    setLevel2Rows(updatedRows);
  };

  // Build config object from current form state (for Save and Insert). Returns null if form invalid.
  const buildConfigFromForm = () => {
    const hasLevel1 = level1SelectionType === 'equation'
      ? formLevel1Equation?.expression
      : level1SelectionType === 'metric'
        ? selectedLevel1Metric
        : level1SelectionType === 'datapoint'
          ? selectedLevel1Datapoint
          : false;
    const hasLevel2 = level2Rows.length > 0 && level2Rows.every(row => row.label.trim() && (row.metric || row.datapoint));
    // When level1 is Equation, level2 is not required (associate with level1)
    const requiresLevel2 = level1SelectionType !== 'equation';
    if (!formName.trim() || !hasLevel1 || (requiresLevel2 && !hasLevel2)) return null;

    const level1 = level1SelectionType === 'metric' && selectedLevel1Metric
      ? { ...selectedLevel1Metric, itemType: 'metric' }
      : level1SelectionType === 'datapoint' && selectedLevel1Datapoint
        ? { ...selectedLevel1Datapoint, itemType: 'datapoint' }
        : null;

    const level2 = level2Rows
      .filter(row => row.metric || row.datapoint)
      .map(row => {
        if (row.metric) {
          return {
            ...row.metric,
            itemType: 'metric',
            label: row.label.trim(),
            drillDownEnabled: row.drillDownEnabled || false,
            drillDownType: row.drillDownType || 'equation'
          };
        } else if (row.datapoint) {
          return {
            ...row.datapoint,
            itemType: 'datapoint',
            label: row.label.trim(),
            drillDownEnabled: row.drillDownEnabled || false,
            drillDownType: row.drillDownType || 'equation'
          };
        }
        return null;
      })
      .filter(item => item !== null);

    const level2Metrics = level2Rows.filter(row => row.metric).map(row => row.metric);
    const level2Datapoints = level2Rows.filter(row => row.datapoint).map(row => row.datapoint);

    return {
      id: Date.now(),
      name: formName.trim(),
      level1Metric: level1SelectionType === 'metric' ? selectedLevel1Metric : null,
      level1Datapoint: level1SelectionType === 'datapoint' ? selectedLevel1Datapoint : null,
      level2Rows: level2Rows.map(cloneLevel2RowForConfig),
      level2Metrics,
      level2Datapoints,
      level1: level1SelectionType === 'equation' ? null : level1,
      level2: level2,
      useLevel1Equation: level1SelectionType === 'equation',
      level1Equation: level1SelectionType === 'equation' ? formLevel1Equation : null,
      level1VerticalCalcEnabled: level1SelectionType === 'equation' ? formLevel1VerticalCalcEnabled : false,
      showPercentage: showPercentage
    };
  };

  const handleInsertConfig = () => {
    const newConfig = buildConfigFromForm();
    if (!newConfig) {
      return;
    }

    setConfigRows(withSortOrders([...configRows, newConfig]));

    setFormName('');
    setSelectedLevel1Metric(null);
    setSelectedLevel1Datapoint(null);
    setLevel2Rows([]);
    setFormLevel1Equation(null);
    setFormLevel1VerticalCalcEnabled(false);
    setShowPercentage(true);
  };

  // Equation handlers
  const handleOpenEquation = (configIndex, rowId, level) => {
    setOpenEquationIndex({
      configIndex,
      rowId,
      level
    });
  };

  const handleCloseEquation = () => {
    setOpenEquationIndex({
      configIndex: null,
      rowId: null,
      level: null
    });
  };

  const hasEquationContent = (equationObj) => {
    if (!equationObj) return false;
    const exp = equationObj.expression == null ? '' : String(equationObj.expression).trim();
    const expWithId = equationObj.expressionWithId == null ? '' : String(equationObj.expressionWithId).trim();
    return Boolean(exp || expWithId);
  };

  const closeEquationBuilderIfOpen = (predicate) => {
    if (predicate(openEquationIndex)) {
      handleCloseEquation();
    }
  };

  const handleClearFormLevel1Equation = () => {
    closeEquationBuilderIfOpen(({ configIndex, level }) => configIndex === null && level === 'formLevel1');
    setFormLevel1Equation(null);
    setFormLevel1VerticalCalcEnabled(false);
    setLevel1SelectionType('metric');
    setTimeout(() => {
      document.getElementById('form-level1-metric-radio')?.focus();
    }, 0);
  };

  const handleClearConfigLevel1Equation = (configIndex, configId) => {
    closeEquationBuilderIfOpen(({ configIndex: ci, level }) => ci === configIndex && level === 'level1');
    const updatedRows = [...configRows];
    const row = updatedRows[configIndex];
    if (!row) return;
    updatedRows[configIndex] = {
      ...row,
      useLevel1Equation: false,
      level1Equation: null,
      level1VerticalCalcEnabled: false,
      level1SelectionType: 'metric',
      level1Metric: null,
      level1Datapoint: null,
      level1: null
    };
    setConfigRows(updatedRows);
    setTimeout(() => {
      document.getElementById(`config-level1-metric-radio-${configId}`)?.focus();
    }, 0);
  };

  const handleClearFormLevel2Equation = (rowId) => {
    closeEquationBuilderIfOpen(
      ({ configIndex, rowId: rid, level }) => configIndex === null && rid === rowId && level === 'formLevel2'
    );
    setLevel2Rows(rows =>
      rows.map(r =>
        r.id === rowId
          ? {
              ...r,
              useEquation: false,
              equation: null,
              level2SelectionType: 'metric',
              metric: null,
              datapoint: null
            }
          : r
      )
    );
    setTimeout(() => {
      document.getElementById(`form-level2-metric-radio-${rowId}`)?.focus();
    }, 0);
  };

  const handleClearConfigLevel2Equation = (configIndex, rowId) => {
    closeEquationBuilderIfOpen(
      ({ configIndex: ci, rowId: rid, level }) => ci === configIndex && rid === rowId && level === 'level2'
    );
    const updatedRows = [...configRows];
    const cfg = updatedRows[configIndex];
    if (!cfg?.level2Rows) return;
    const rowIndex = cfg.level2Rows.findIndex(r => r.id === rowId);
    if (rowIndex < 0) return;
    updatedRows[configIndex] = {
      ...cfg,
      level2Rows: cfg.level2Rows.map((r, i) =>
        i === rowIndex
          ? {
              ...r,
              useEquation: false,
              equation: null,
              level2SelectionType: 'metric',
              metric: null,
              datapoint: null
            }
          : r
      )
    };
    setConfigRows(updatedRows);
    setTimeout(() => {
      document.getElementById(`level2-metric-radio-${rowId}`)?.focus();
    }, 0);
  };

  const handleEquationUpdate = (equationData) => {
    const { configIndex, rowId, level } = openEquationIndex;

    if (level === 'level1') {
      const updatedRows = [...configRows];
      updatedRows[configIndex].level1Equation = {
        expression: equationData.expression,
        expressionWithId: equationData.expressionWithId
      };
      setConfigRows(updatedRows);
    } else if (level === 'level2' && rowId) {
      const updatedRows = [...configRows];
      const rowIndex = updatedRows[configIndex].level2Rows.findIndex(r => r.id === rowId);
      if (rowIndex >= 0) {
        updatedRows[configIndex].level2Rows[rowIndex].equation = {
          expression: equationData.expression,
          expressionWithId: equationData.expressionWithId
        };
      }
      setConfigRows(updatedRows);
    } else if (level === 'formLevel1') {
      setFormLevel1Equation({
        expression: equationData.expression,
        expressionWithId: equationData.expressionWithId
      });
    } else if (level === 'formLevel2' && rowId) {
      const updatedRows = level2Rows.map(row =>
        row.id === rowId
          ? { ...row, equation: { expression: equationData.expression, expressionWithId: equationData.expressionWithId } }
          : row
      );
      setLevel2Rows(updatedRows);
    }

    handleCloseEquation();
  };

  const handleParentEntityChange = (entityId, checked) => {
    let updatedEntities = [...filter.parentEntities];
    if (checked) {
      if (!updatedEntities.includes(entityId)) {
        updatedEntities.push(entityId);
      }
    } else {
      updatedEntities = updatedEntities.filter(id => id !== entityId);
    }
    const newFilter = {
      ...filter,
      parentEntities: updatedEntities
    };
    setFilter(newFilter);
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  const handleParentEntitySelectAll = () => {
    const allParentIds = parentEntities.map(entity => entity.id);
    const newFilter = {
      ...filter,
      parentEntities: allParentIds
    };
    setFilter(newFilter);
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  const handleParentEntityUnselectAll = () => {
    const newFilter = {
      ...filter,
      parentEntities: []
    };
    setFilter(newFilter);
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  const isParentEntitySelected = (entityId) => {
    return filter.parentEntities.includes(Number(entityId)) || filter.parentEntities.includes(String(entityId));
  };

  const getSelectedParentEntitiesCount = () => {
    return filter.parentEntities.length;
  };

  const areAllParentEntitiesSelected = () => {
    return parentEntities.length > 0 && filter.parentEntities.length === parentEntities.length;
  };

  const areNoParentEntitiesSelected = () => {
    return filter.parentEntities.length === 0;
  };

  const handleChildEntityChange = (entityId, checked) => {
    let updatedEntities = [...filter.childEntities];
    if (checked) {
      if (!updatedEntities.includes(entityId)) {
        updatedEntities.push(entityId);
      }
    } else {
      updatedEntities = updatedEntities.filter(id => id !== entityId);
    }
    const newFilter = {
      ...filter,
      childEntities: updatedEntities
    };
    setFilter(newFilter);
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  const handleChildEntitySelectAll = () => {
    const allChildIds = childEntities.map(entity => entity.id);
    const newFilter = {
      ...filter,
      childEntities: allChildIds
    };
    setFilter(newFilter);
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  const handleChildEntityUnselectAll = () => {
    const newFilter = {
      ...filter,
      childEntities: []
    };
    setFilter(newFilter);
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  const isChildEntitySelected = (entityId) => {
    return filter.childEntities.includes(Number(entityId)) || filter.childEntities.includes(String(entityId));
  };

  const getSelectedChildEntitiesCount = () => {
    return filter.childEntities.length;
  };

  const areAllChildEntitiesSelected = () => {
    return childEntities.length > 0 && filter.childEntities.length === childEntities.length;
  };

  const areNoChildEntitiesSelected = () => {
    return filter.childEntities.length === 0;
  };

  // Helper function to check if entity has children (is a parent)
  const hasChildren = (entity) => {
    return entity.children && entity.children.length > 0;
  };

  // Helper function to check if entity is selected (either as parent or child)
  const isEntitySelected = (entityId) => {
    const id = Number(entityId);
    return filter.parentEntities.includes(id) || filter.parentEntities.includes(String(id)) ||
      filter.childEntities.includes(id) || filter.childEntities.includes(String(id));
  };

  // Helper function to determine if entity should be in parentEntities or childEntities
  const isParentEntity = (entity) => {
    // An entity is a parent if it has children
    return hasChildren(entity);
  };

  /** Apply single-entity check/uncheck to parent/child id lists (same rules as tree checkbox). */
  const applyEntityToggleToArrays = (parentEntities, childEntities, entity, checked) => {
    const entityId = entity.id;
    const isParent = isParentEntity(entity);
    let updatedParentEntities = [...parentEntities];
    let updatedChildEntities = [...childEntities];

    if (checked) {
      if (isParent) {
        if (!updatedParentEntities.includes(entityId)) {
          updatedParentEntities.push(entityId);
        }
        updatedChildEntities = updatedChildEntities.filter(id => id !== entityId);
      } else {
        if (!updatedChildEntities.includes(entityId)) {
          updatedChildEntities.push(entityId);
        }
        updatedParentEntities = updatedParentEntities.filter(id => id !== entityId);
      }
    } else {
      updatedParentEntities = updatedParentEntities.filter(id => id !== entityId);
      updatedChildEntities = updatedChildEntities.filter(id => id !== entityId);
    }
    return { parentEntities: updatedParentEntities, childEntities: updatedChildEntities };
  };

  const emitHierarchyFilter = (updatedParentEntities, updatedChildEntities) => {
    const newFilter = {
      ...filter,
      parentEntities: updatedParentEntities,
      childEntities: updatedChildEntities
    };
    setFilter(newFilter);
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  // Toggle entity selection
  const handleEntityToggle = (entity, checked) => {
    const { parentEntities: pe, childEntities: ce } = applyEntityToggleToArrays(
      filter.parentEntities,
      filter.childEntities,
      entity,
      checked
    );
    emitHierarchyFilter(pe, ce);
  };

  /** Select every top-level organisation node (respecting parent vs leaf placement). */
  const handleTreeRootSelectAll = () => {
    let pe = [...filter.parentEntities];
    let ce = [...filter.childEntities];
    [...enterpriseStructure]
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      .forEach((entity) => {
        const next = applyEntityToggleToArrays(pe, ce, entity, true);
        pe = next.parentEntities;
        ce = next.childEntities;
      });
    emitHierarchyFilter(pe, ce);
  };

  /** Clear selection for all top-level organisation nodes only. */
  const handleTreeRootUnselectAll = () => {
    const rootIdSet = new Set(enterpriseStructure.map((e) => String(e.id)));
    const pe = filter.parentEntities.filter((id) => !rootIdSet.has(String(id)));
    const ce = filter.childEntities.filter((id) => !rootIdSet.has(String(id)));
    emitHierarchyFilter(pe, ce);
  };

  const areAllRootTreeEntitiesSelected = () => {
    const roots = [...enterpriseStructure].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    if (roots.length === 0) return false;
    return roots.every((e) => isEntitySelected(e.id));
  };

  const areNoRootTreeEntitiesSelected = () => {
    const roots = [...enterpriseStructure].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    if (roots.length === 0) return true;
    return roots.every((e) => !isEntitySelected(e.id));
  };

  /** Select all direct children of an expanded node (nested groups supported). */
  const handleTreeDirectChildrenSelectAll = (parentEntity) => {
    const children = parentEntity?.children;
    if (!children || children.length === 0) return;
    let pe = [...filter.parentEntities];
    let ce = [...filter.childEntities];
    [...children]
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      .forEach((child) => {
        const next = applyEntityToggleToArrays(pe, ce, child, true);
        pe = next.parentEntities;
        ce = next.childEntities;
      });
    emitHierarchyFilter(pe, ce);
  };

  /** Unselect all direct children of a node. */
  const handleTreeDirectChildrenUnselectAll = (parentEntity) => {
    const children = parentEntity?.children;
    if (!children || children.length === 0) return;
    const directIdSet = new Set(children.map((c) => String(c.id)));
    const pe = filter.parentEntities.filter((id) => !directIdSet.has(String(id)));
    const ce = filter.childEntities.filter((id) => !directIdSet.has(String(id)));
    emitHierarchyFilter(pe, ce);
  };

  const areAllDirectChildrenSelected = (parentEntity) => {
    const children = parentEntity?.children;
    if (!children || children.length === 0) return true;
    return [...children]
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      .every((child) => isEntitySelected(child.id));
  };

  const areNoDirectChildrenSelected = (parentEntity) => {
    const children = parentEntity?.children;
    if (!children || children.length === 0) return true;
    return [...children]
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      .every((child) => !isEntitySelected(child.id));
  };

  // Toggle expand/collapse
  const toggleEntityExpand = (entityId) => {
    setExpandedEntities(prev => ({
      ...prev,
      [entityId]: !prev[entityId]
    }));
  };

  // Recursive function to render entity tree
  const renderEntityTree = (entities, depth = 0) => {
    if (!entities || entities.length === 0) return null;

    return (
      <ul className={`esg-entity-tree ${depth === 0 ? 'esg-entity-tree-root' : ''}`} style={{ paddingLeft: depth > 0 ? '20px' : '0' }}>
        {entities
          .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
          .map((entity) => {
            const hasChildrenNodes = hasChildren(entity);
            const isExpanded = expandedEntities[entity.id] || false;
            const isSelected = isEntitySelected(entity.id);
            const isParent = isParentEntity(entity);

            return (
              <li key={entity.id} className="esg-entity-tree-item">
                <div className="esg-entity-tree-node d-flex align-items-center">
                  {hasChildrenNodes && (
                    <button
                      type="button"
                      className="esg-entity-tree-toggle btn btn-link p-0 me-2"
                      onClick={() => toggleEntityExpand(entity.id)}
                      style={{ minWidth: '20px', textAlign: 'left' }}
                    >
                      {isExpanded ? '▼' : '▶'}
                    </button>
                  )}
                  {!hasChildrenNodes && <span className="esg-entity-tree-spacer me-2" style={{ width: '20px' }} />}
                  <Form.Check
                    type="checkbox"
                    id={`entity-${entity.id}`}
                    checked={isSelected}
                    onChange={(e) => handleEntityToggle(entity, e.target.checked)}
                    className="me-2"
                  />
                  <label htmlFor={`entity-${entity.id}`} className="mb-0 cursor-pointer flex-grow-1">
                    {entity.name}
                  </label>
                </div>
                {hasChildrenNodes && isExpanded && (
                  <div className="esg-entity-tree-children">
                    <div className="esg-entity-tree-bulk-actions d-flex flex-wrap align-items-center mb-1 ps-1">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary ghg-select-all-btn"
                        onClick={() => handleTreeDirectChildrenSelectAll(entity)}
                        disabled={areAllDirectChildrenSelected(entity)}
                      >
                        {t('SelectAll.label')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary ghg-unselect-all-btn"
                        onClick={() => handleTreeDirectChildrenUnselectAll(entity)}
                        disabled={areNoDirectChildrenSelected(entity)}
                      >
                        {t('UnselectAll.label')}
                      </button>
                    </div>
                    {renderEntityTree(entity.children, depth + 1)}
                  </div>
                )}
              </li>
            );
          })}
      </ul>
    );
  };

  const handlePeriodSelectAll = () => {
    setSelectedData([...period]);
    preparePeriod([...period]);
  };

  const handlePeriodUnselectAll = () => {
    setSelectedData([]);
    preparePeriod([]);
  };

  const isPeriodSelected = (periodItem) => {
    return selectedData.some(item => item.id === periodItem.id);
  };

  const getSelectedPeriodsCount = () => {
    return selectedData.length;
  };

  const areAllPeriodsSelected = () => {
    return period.length > 0 && selectedData.length === period.length;
  };

  const areNoPeriodsSelected = () => {
    return selectedData.length === 0;
  };

  const getCategoryDisplayText = () => {
    const category = CategoryList({ t }).find(c => c.id === filter.categoryType);
    return category ? category.text : 'All';
  };

  const getPeriodTypeDisplayText = () => {
    switch (filter.periodType) {
      case 'YEARLY':
        return t("Yearly.label");
      case 'QUARTERLY':
        return t("Quarterly.label");
      case 'MONTHLY':
        return t("Monthly.label");
      default:
        return t("Yearly.label");
    }
  };



  const renderCategoryDropdown = () => (
    <>
      <div className="ghg-header-pill">
        <Dropdown show={showCategoryDropdown} onToggle={(isOpen) => setShowCategoryDropdown(isOpen)}>
          <Dropdown.Toggle
            id="dropdown-category"
            className="ghg-dropdown-toggle"
          >
            Category: {getCategoryDisplayText()}
          </Dropdown.Toggle>
          <Dropdown.Menu className="p-2 ghg-dropdown-menu">
            {CategoryList({ t }).map(c => (
              <div key={c.id} className="d-flex align-items-center">
                <Form.Check
                  type="radio"
                  id={`category-${c.id}`}
                  checked={filter.categoryType === c.id}
                  onChange={() => {
                    const newFilter = { ...filter, categoryType: c.id, topicIds: [] };
                    setFilter(newFilter);
                    setShowCategoryDropdown(false);
                    if (onFilterChange) {
                      onFilterChange(newFilter);
                    }
                  }}
                  className="me-2"
                />
                <label htmlFor={`category-${c.id}`} className="mb-0 cursor-pointer">
                  {c.text}
                </label>
              </div>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <div className="ghg-header-divider">|</div>
    </>
  );

  const renderTopicDropdown = () => (
    <>
      <div className="ghg-header-pill">
        <Dropdown show={showTopicDropdown} onToggle={(isOpen) => setShowTopicDropdown(isOpen)}>
          <Dropdown.Toggle
            id="dropdown-topic"
            className="ghg-dropdown-toggle"
          >
            Topic {getSelectedTopicsCount() > 0 && `(${getSelectedTopicsCount()})`}
          </Dropdown.Toggle>
          <Dropdown.Menu className="p-2 ghg-dropdown-menu ghg-dropdown-menu-wide">
            <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
              <button
                type="button"
                className="btn btn-sm btn-primary ghg-select-all-btn"
                onClick={handleTopicSelectAll}
                disabled={areAllTopicsSelected()}
              >
                {t("SelectAll.label")}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary ghg-unselect-all-btn"
                onClick={handleTopicUnselectAll}
                disabled={areNoTopicsSelected()}
              >
                {t("UnselectAll.label")}
              </button>
            </div>
            <div className="ghg-dropdown-scrollable">
              {topics.map((topic) => (
                <div key={topic.id} className="d-flex align-items-center mb-1">
                  <Form.Check
                    type="checkbox"
                    id={`topic-${topic.id}`}
                    checked={isTopicSelected(topic.id)}
                    onChange={(e) => handleTopicChange(topic.id, e.target.checked)}
                    className="me-2"
                  />
                  <label htmlFor={`topic-${topic.id}`} className="mb-0 cursor-pointer">
                    {topic.name}
                  </label>
                </div>
              ))}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <div className="ghg-header-divider">|</div>
    </>
  );


  const renderParentEntityDropdown = () => (
    <>
      <div className="ghg-header-pill">
        <Dropdown show={showParentEntityDropdown} onToggle={(isOpen) => setShowParentEntityDropdown(isOpen)}>
          <Dropdown.Toggle
            id="dropdown-parent-entity"
            className="ghg-dropdown-toggle"
          >
            Parent Entity {getSelectedParentEntitiesCount() > 0 && `(${getSelectedParentEntitiesCount()})`}
          </Dropdown.Toggle>
          <Dropdown.Menu className="p-2 ghg-dropdown-menu ghg-dropdown-menu-wide">
            <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
              <button
                type="button"
                className="btn btn-sm btn-primary ghg-select-all-btn"
                onClick={handleParentEntitySelectAll}
                disabled={areAllParentEntitiesSelected()}
              >
                {t("SelectAll.label")}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary ghg-unselect-all-btn"
                onClick={handleParentEntityUnselectAll}
                disabled={areNoParentEntitiesSelected()}
              >
                {t("UnselectAll.label")}
              </button>
            </div>
            <div className="ghg-dropdown-scrollable">
              {parentEntities.map((pEnt) => (
                <div key={pEnt.id} className="d-flex align-items-center mb-1">
                  <Form.Check
                    type="checkbox"
                    id={`parent-${pEnt.id}`}
                    checked={isParentEntitySelected(pEnt.id)}
                    onChange={(e) => handleParentEntityChange(pEnt.id, e.target.checked)}
                    className="me-2"
                  />
                  <label htmlFor={`parent-${pEnt.id}`} className="mb-0 cursor-pointer">
                    {pEnt?.name}
                  </label>
                </div>
              ))}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <div className="ghg-header-divider">|</div>
    </>
  );

  const renderChildEntityDropdown = () => (
    <>
      <div className="ghg-header-pill">
        <Dropdown show={showChildEntityDropdown} onToggle={(isOpen) => setShowChildEntityDropdown(isOpen)}>
          <Dropdown.Toggle
            id="dropdown-child-entity"
            className="ghg-dropdown-toggle"
          >
            Child Entity {getSelectedChildEntitiesCount() > 0 && `(${getSelectedChildEntitiesCount()})`}
          </Dropdown.Toggle>
          <Dropdown.Menu className="p-2 ghg-dropdown-menu ghg-dropdown-menu-wide">
            <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
              <button
                type="button"
                className="btn btn-sm btn-primary ghg-select-all-btn"
                onClick={handleChildEntitySelectAll}
                disabled={areAllChildEntitiesSelected()}
              >
                {t("SelectAll.label")}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary ghg-unselect-all-btn"
                onClick={handleChildEntityUnselectAll}
                disabled={areNoChildEntitiesSelected()}
              >
                {t("UnselectAll.label")}
              </button>
            </div>
            <div className="ghg-dropdown-scrollable">
              {childEntities.map((cEnt) => (
                <div key={cEnt.id} className="d-flex align-items-center mb-1">
                  <Form.Check
                    type="checkbox"
                    id={`child-${cEnt.id}`}
                    checked={isChildEntitySelected(cEnt.id)}
                    onChange={(e) => handleChildEntityChange(cEnt.id, e.target.checked)}
                    className="me-2"
                  />
                  <label htmlFor={`child-${cEnt.id}`} className="mb-0 cursor-pointer">
                    {cEnt?.name}
                  </label>
                </div>
              ))}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <div className="ghg-header-divider">|</div>
    </>
  );

  const renderPeriodTypeDropdown = () => (
    <>
      <div className="ghg-header-pill">
        <Dropdown show={showPeriodTypeDropdown} onToggle={(isOpen) => setShowPeriodTypeDropdown(isOpen)}>
          <Dropdown.Toggle
            id="dropdown-period-type"
            className="ghg-dropdown-toggle"
          >
            {getPeriodTypeDisplayText()}
          </Dropdown.Toggle>
          <Dropdown.Menu className="p-2">
            <div className="d-flex align-items-center">
              <Form.Check
                type="radio"
                id="period-yearly"
                checked={filter.periodType === 'YEARLY'}
                onChange={() => {
                  const newFilter = { ...filter, periodType: 'YEARLY', period: null };
                  setFilter(newFilter);
                  setPeriodItems('direct', 'YEARLY');
                  setShowPeriodTypeDropdown(false);
                  if (onFilterChange) {
                    onFilterChange(newFilter);
                  }
                }}
                className="me-2"
              />
              <label htmlFor="period-yearly" className="mb-0 cursor-pointer">
                {t("Yearly.label")}
              </label>
            </div>
            <div className="d-flex align-items-center">
              <Form.Check
                type="radio"
                id="period-quarterly"
                checked={filter.periodType === 'QUARTERLY'}
                onChange={() => {
                  const newFilter = { ...filter, periodType: 'QUARTERLY', period: null };
                  setFilter(newFilter);
                  setPeriodItems('direct', 'QUARTERLY');
                  setShowPeriodTypeDropdown(false);
                  if (onFilterChange) {
                    onFilterChange(newFilter);
                  }
                }}
                className="me-2"
              />
              <label htmlFor="period-quarterly" className="mb-0 cursor-pointer">
                {t("Quarterly.label")}
              </label>
            </div>
            <div className="d-flex align-items-center">
              <Form.Check
                type="radio"
                id="period-monthly"
                checked={filter.periodType === 'MONTHLY'}
                onChange={() => {
                  const newFilter = { ...filter, periodType: 'MONTHLY', period: null };
                  setFilter(newFilter);
                  setPeriodItems('direct', 'MONTHLY');
                  setShowPeriodTypeDropdown(false);
                  if (onFilterChange) {
                    onFilterChange(newFilter);
                  }
                }}
                className="me-2"
              />
              <label htmlFor="period-monthly" className="mb-0 cursor-pointer">
                {t("Monthly.label")}
              </label>
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <div className="ghg-header-divider">|</div>
    </>
  );

  const renderReportingPeriodDropdown = () => {
    const groupedPeriods = {};
    period.forEach(item => {
      const category = item.category || 'Years';
      if (!groupedPeriods[category]) {
        groupedPeriods[category] = [];
      }
      groupedPeriods[category].push(item);
    });
    const companyInfo = parseCompanyInfoFromStorage();
    const isFinancialYear =
      (companyInfo?.frequencyType ?? companyInfo?.dataFrequencyType) === 'FY';
    const showYearlyFyRange = filter.periodType === 'YEARLY' && isFinancialYear;

    return (
      <div className="ghg-header-pill">
        <Dropdown show={showPeriodDropdown} onToggle={(isOpen) => setShowPeriodDropdown(isOpen)}>
          <Dropdown.Toggle
            id="dropdown-period"
            className="ghg-dropdown-toggle"
          >
            Reporting Period {getSelectedPeriodsCount() > 0 && `(${getSelectedPeriodsCount()})`}
          </Dropdown.Toggle>
          <Dropdown.Menu className="p-2 ghg-dropdown-menu ghg-dropdown-menu-wide">
            <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
              <button
                type="button"
                className="btn btn-sm btn-primary ghg-select-all-btn"
                onClick={handlePeriodSelectAll}
                disabled={areAllPeriodsSelected()}
              >
                {t("SelectAll.label")}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary ghg-unselect-all-btn"
                onClick={handlePeriodUnselectAll}
                disabled={areNoPeriodsSelected()}
              >
                {t("UnselectAll.label")}
              </button>
            </div>
            <div className="ghg-dropdown-scrollable">
              {Object.keys(groupedPeriods).sort((a, b) => {
                if (filter.periodType === 'YEARLY') {
                  return parseInt(b) - parseInt(a);
                }
                return b.localeCompare(a);
              }).map(category => (
                <div key={category}>
                  {filter.periodType !== 'YEARLY' && (
                    <div className="d-flex align-items-center mb-2 mt-2 ghg-period-category-label">
                      {filter.periodType === 'MONTHLY' && (
                        <Form.Check
                          type="checkbox"
                          id={`edit-period-year-${category}`}
                          data-testid={`esg-compare-year-select-${category}`}
                          checked={areAllMonthsSelectedForYear(category)}
                          onChange={() => toggleYearMonthsSelection(category)}
                          className="me-2"
                          aria-label={t('ESGAnalytics.yearSelectAllMonthsAria.label', { year: category })}
                        />
                      )}
                      <span className="fw-bold text-muted">{category}</span>
                    </div>
                  )}
                  {groupedPeriods[category].map((periodItem) => (
                    <div
                      key={periodItem.id}
                      className="d-flex align-items-center mb-1 gap-1 flex-wrap esg-yearly-period-row"
                    >
                      <div className="d-flex align-items-center min-w-0">
                        <Form.Check
                          type="checkbox"
                          id={`period-${periodItem.id}`}
                          checked={isPeriodSelected(periodItem)}
                          onChange={(e) => onSelectDeSelectPeriod(periodItem)}
                          className="me-2"
                        />
                        <label htmlFor={`period-${periodItem.id}`} className="mb-0 cursor-pointer">
                          {periodItem.displayValue}
                        </label>
                      </div>
                      {showYearlyFyRange && (
                        <span className="esg-yearly-fy-range">
                          {`(${getCompareYearPeriodRangeLabel(t, periodItem.value, companyInfo)})`}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  // Render frequency as on-screen interactive controls (view mode only)
  const renderFrequencyDisplay = () => {
    if (editMode) return null;

    return (
      <div className="esg-filter-display-item">
        <div className="esg-filter-label d-flex align-items-center gap-1">
          {t("Frequency.label") || "Frequency"}
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="esg-data-collection-frequency-tooltip">
                {t("ESGAnalytics.dataCollectionFrequency.label")}
              </Tooltip>
            }
          >
            <span className="esg-analytics-frequency-info-icon" aria-label={t("ESGAnalytics.dataCollectionFrequency.label")}>
              <BiInfoCircle size={14} />
            </span>
          </OverlayTrigger>
        </div>
        <div className="esg-filter-controls">
          <div className="d-flex flex-column gap-2">
            <div className="d-flex align-items-center">
              <Form.Check
                type="radio"
                id="view-period-yearly"
                name="view-period-type"
                checked={filter.periodType === 'YEARLY'}
                onChange={() => {
                  const newFilter = { ...filter, periodType: 'YEARLY', period: null };
                  setFilter(newFilter);
                  setPeriodItems('direct', 'YEARLY');
                  if (onFilterChange) {
                    onFilterChange(newFilter);
                  }
                }}
                className="me-2"
              />
              <label htmlFor="view-period-yearly" className="mb-0 cursor-pointer">
                {t("Yearly.label")}
              </label>
            </div>
            <div className="d-flex align-items-center">
              <Form.Check
                type="radio"
                id="view-period-quarterly"
                name="view-period-type"
                checked={filter.periodType === 'QUARTERLY'}
                onChange={() => {
                  const newFilter = { ...filter, periodType: 'QUARTERLY', period: null };
                  setFilter(newFilter);
                  setPeriodItems('direct', 'QUARTERLY');
                  if (onFilterChange) {
                    onFilterChange(newFilter);
                  }
                }}
                className="me-2"
              />
              <label htmlFor="view-period-quarterly" className="mb-0 cursor-pointer">
                {t("Quarterly.label")}
              </label>
            </div>
            <div className="d-flex align-items-center">
              <Form.Check
                type="radio"
                id="view-period-monthly"
                name="view-period-type"
                checked={filter.periodType === 'MONTHLY'}
                onChange={() => {
                  const newFilter = { ...filter, periodType: 'MONTHLY', period: null };
                  setFilter(newFilter);
                  setPeriodItems('direct', 'MONTHLY');
                  if (onFilterChange) {
                    onFilterChange(newFilter);
                  }
                }}
                className="me-2"
              />
              <label htmlFor="view-period-monthly" className="mb-0 cursor-pointer">
                {t("Monthly.label")}
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render reporting period as on-screen interactive controls (view mode only)
  const renderReportingPeriodDisplay = () => {
    if (editMode) return null;

    const groupedPeriods = {};
    period.forEach(item => {
      const category = item.category || 'Years';
      if (!groupedPeriods[category]) {
        groupedPeriods[category] = [];
      }
      groupedPeriods[category].push(item);
    });
    const companyInfo = parseCompanyInfoFromStorage();
    const isFinancialYear =
      (companyInfo?.frequencyType ?? companyInfo?.dataFrequencyType) === 'FY';
    const showYearlyFyRange = filter.periodType === 'YEARLY' && isFinancialYear;

    return (
      <div className="esg-filter-display-item">
        <div className="esg-filter-label d-flex align-items-center gap-1">
          {t("ReportingPeriod.label") || "Reporting Period"}
          {selectedData.length > 0 && ` (${selectedData.length})`}
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="esg-reporting-period-of-data-tooltip">
                {t("ESGAnalytics.reportingPeriodOfData.label")}
              </Tooltip>
            }
          >
            <span className="esg-analytics-frequency-info-icon" aria-label={t("ESGAnalytics.reportingPeriodOfData.label")}>
              <BiInfoCircle size={14} />
            </span>
          </OverlayTrigger>
        </div>
        <div className="esg-filter-controls">
          {period.length > 0 ? (
            <>

              <div className="esg-filter-periods-list">
                {Object.keys(groupedPeriods).sort((a, b) => {
                  if (filter.periodType === 'YEARLY') {
                    return parseInt(b) - parseInt(a);
                  }
                  return b.localeCompare(a);
                }).map(category => (
                  <div key={category} className="esg-period-category-group">
                    {filter.periodType !== 'YEARLY' && (
                      <div className="d-flex align-items-center mb-2 mt-2 esg-period-category-label">
                        {filter.periodType === 'MONTHLY' && (
                          <Form.Check
                            type="checkbox"
                            id={`view-period-year-${category}`}
                            data-testid={`esg-compare-year-select-view-${category}`}
                            checked={areAllMonthsSelectedForYear(category)}
                            onChange={() => toggleYearMonthsSelection(category)}
                            className="me-2"
                            aria-label={t('ESGAnalytics.yearSelectAllMonthsAria.label', { year: category })}
                          />
                        )}
                        <span className="fw-bold text-muted">{category}</span>
                      </div>
                    )}
                    {groupedPeriods[category].map((periodItem) => (
                      <div
                        key={periodItem.id}
                        className="d-flex align-items-center mb-2 gap-1 flex-wrap esg-yearly-period-row"
                      >
                        <div className="d-flex align-items-center min-w-0">
                          <Form.Check
                            type="checkbox"
                            id={`view-period-${periodItem.id}`}
                            checked={isPeriodSelected(periodItem)}
                            onChange={(e) => onSelectDeSelectPeriod(periodItem)}
                            className="me-2"
                          />
                          <label htmlFor={`view-period-${periodItem.id}`} className="mb-0 cursor-pointer">
                            {periodItem.displayValue}
                          </label>
                        </div>
                        {showYearlyFyRange && (
                          <span className="esg-yearly-fy-range">
                            {`(${getCompareYearPeriodRangeLabel(t, periodItem.value, companyInfo)})`}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="esg-filter-empty text-muted">
              {t("NoPeriodsAvailable.label") || "No periods available"}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Config rows to pass to Save: include current form as one object if form is valid (so Save is active before "Insert new config below").
  const getEffectiveConfigRowsForSave = () => {
    const formConfig = buildConfigFromForm();
    if (formConfig) {
      return withSortOrders([formConfig, ...configRows]);
    }
    return withSortOrders([...configRows]);
  };

  const rowHasLevel1AndLevel2 = (row) => {
    const hasName = !!(row.name && typeof row.name === 'string' && row.name.trim());
    const hasLevel1 = !!(
      row.level1 ||
      row.level1Metric ||
      row.level1Datapoint ||
      (row.useLevel1Equation && row.level1Equation && (row.level1Equation.expressionWithId || row.level1Equation.expression))
    );
    const isLevel1Equation = !!(row.useLevel1Equation && row.level1Equation && (row.level1Equation.expressionWithId || row.level1Equation.expression));
    const hasLevel2 = !!(
      row.level2Rows &&
      row.level2Rows.length > 0 &&
      row.level2Rows.every(r =>
        (r.label && r.label.trim()) &&
        (r.metric || r.datapoint || (r.useEquation && r.equation && (r.equation.expressionWithId || r.equation.expression)))
      )
    );
    // When level1 is Equation, level2 is not required (associate with level1)
    return hasName && hasLevel1 && (isLevel1Equation || hasLevel2);
  };

  const isSaveEnabled = () => {
    if (!title.trim()) return false;
    const effective = getEffectiveConfigRowsForSave();
    if (effective.length === 0) return false;
    return effective.every(rowHasLevel1AndLevel2);
  };

  // Save button enabled when title and at least one config row (stays enabled after "Add New Level1 Data")
  const isSaveButtonEnabled = () => {
    if (!title.trim()) return false;
    return getEffectiveConfigRowsForSave().length > 0;
  };

  const renderApplyFiltersButton = () => {
    const hasPeriod = filter.period && Object.keys(filter.period).length > 0;
    return (
      <div className="ghg-header-filters-apply-btn">
        {editMode && (
          <Button
            variant="success"
            onClick={() => {
              if (!onSave) return;
              if (!isSaveEnabled()) {
                setShowValidationState(true);
                toast.error(t("ESGAnalytics.fillRequiredFields.label") || "Please fill required fields");
                return;
              }
              onSave(title, getEffectiveConfigRowsForSave(), filter);
            }}
            disabled={!isSaveButtonEnabled()}
            className="fw-medium"
            style={{ minWidth: '120px' }}
          >
            {t("Save.label")}
          </Button>
        )}
        <Button
          variant="primary"
          onClick={() => {
            if (onApplyFilters) {
              onApplyFilters(configRows);
            }
          }}
          disabled={!hasPeriod}
          className="fw-medium"
          style={{ minWidth: '120px' }}
        >
          {t("Apply.label")}
        </Button>

      </div>
    );
  };

  const renderLevel1MetricTypeahead = () => (
    <Typeahead
      id="form-level1-metric-typeahead"
      className="w-100"
      inputProps={{ className: 'ghg-dropdown-toggle text-truncate', style: { minWidth: '200px' } }}
      labelKey={metricDatapointTypeaheadLabel}
      options={allMetrics}
      placeholder={t("ESGAnalytics.selectMetric.label")}
      selected={selectedLevel1Metric ? [selectedLevel1Metric] : []}
      onChange={(sel) => {
        if (sel.length > 0) handleLevel1MetricSelect(sel[0]);
        else setSelectedLevel1Metric(null);
      }}
      clearButton
      disabled={!!selectedLevel1Datapoint}
    />
  );

  const renderLevel1DatapointTypeahead = () => (
    <Typeahead
      id="form-level1-datapoint-typeahead"
      className="w-100"
      inputProps={{ className: 'ghg-dropdown-toggle text-truncate', style: { minWidth: '200px' } }}
      labelKey={metricDatapointTypeaheadLabel}
      options={allDatapoints}
      placeholder={t("ESGAnalytics.selectDatapoint.label")}
      selected={selectedLevel1Datapoint ? [selectedLevel1Datapoint] : []}
      onChange={(sel) => {
        if (sel.length > 0) handleLevel1DatapointSelect(sel[0]);
        else setSelectedLevel1Datapoint(null);
      }}
      clearButton
      disabled={!!selectedLevel1Metric}
    />
  );

  const renderFormLevel2RowMetricTypeahead = (row) => (
    <Typeahead
      id={`form-level2-metric-typeahead-${row.id}`}
      className="w-100"
      inputProps={{ className: 'ghg-dropdown-toggle text-truncate', style: { minWidth: '200px' } }}
      labelKey={metricDatapointTypeaheadLabel}
      options={allMetrics}
      placeholder={t("ESGAnalytics.selectMetric.label")}
      selected={row.metric ? [row.metric] : []}
      onChange={(sel) => {
        if (sel.length > 0) handleLevel2RowMetricSelect(row.id, sel[0]);
        else handleLevel2RowMetricSelect(row.id, null);
      }}
      clearButton
      disabled={!!row.datapoint}
    />
  );

  const renderFormLevel2RowDatapointTypeahead = (row) => (
    <Typeahead
      id={`form-level2-datapoint-typeahead-${row.id}`}
      className="w-100"
      inputProps={{ className: 'ghg-dropdown-toggle text-truncate', style: { minWidth: '200px' } }}
      labelKey={metricDatapointTypeaheadLabel}
      options={allDatapoints}
      placeholder={t("ESGAnalytics.selectDatapoint.label")}
      selected={row.datapoint ? [row.datapoint] : []}
      onChange={(sel) => {
        if (sel.length > 0) handleLevel2RowDatapointSelect(row.id, sel[0]);
        else handleLevel2RowDatapointSelect(row.id, null);
      }}
      clearButton
      disabled={!!row.metric}
    />
  );

  // Render title field for config form
  const renderConfigFormTitle = () => {
    return (
      <div className="mt-3 p-3 Unit_Wise_Card bg-opacity-80 1">
        <div className="mb-2">
          <p>{t("ESGAnalytics.titleFieldDescription.label")}</p>
          <div className="d-flex align-items-center gap-2">
            <label className="mb-1 white-nowrap">{t("ESGAnalytics.nameOfTheWidget.label")} <span className="text-danger">*</span></label>
            <Form.Control
              type="text"
              placeholder={t("ESGAnalytics.enterTitle.label")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-50"
              style={showValidationState && !title.trim() ? { border: '2px solid var(--bs-danger)' } : undefined}
            // size="sm"
            />
          </div>
        </div>
      </div>
    );
  };

  // Render equation display component
  const renderEquationDisplay = (equationObj, onClickHandler, isOpen) => {
    return (
      <div className="AddEquation_box 9">
        <Dropdown
          show={isOpen}
          onToggle={(nextShow) => {
            if (nextShow) {
              onClickHandler();
            } else if (isOpen) {
              handleCloseEquation();
            }
          }}
          autoClose={false}
        >
          <Dropdown.Toggle variant="ShowEquation" id="dropdown-basic" >
            <AiOutlineCalculator className="me-2" />
            {equationObj?.expression || t("SetupGraphEquation.label")}
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="p-0 esg-analytics-equation-dropdown-menu"
            popperConfig={{ strategy: 'fixed' }}
          >
            <EquationBuilder
              stdId={filter.standardId || glbRegulationId}
              metricId={0}
              topicId={0}
              equationObj={equationObj || { expression: '', expressionWithId: '' }}
              buildFor={EquationBuilderFor.ESG_ANALYTICS}
              discardBtnClick={handleCloseEquation}
              updatedEquationObj={handleEquationUpdate}
              topicIds={filter.topicIds}
            />
          </Dropdown.Menu>
        </Dropdown>
        {/* <span className="cursor" onClick={onClickHandler}>
          <AiOutlineCalculator className="me-2" />
          {equationObj?.expression || t("SetupGraphEquation.label")}
        </span>
        {isOpen && (
          <div className="mt-2">
            <EquationBuilder
              stdId={filter.standardId || glbRegulationId}
              metricId={0}
              topicId={0}
              equationObj={equationObj || { expression: '', expressionWithId: '' }}
              buildFor={EquationBuilderFor.ESG_ANALYTICS}
              discardBtnClick={handleCloseEquation}
              updatedEquationObj={handleEquationUpdate}
              topicIds={filter.topicIds}
            />
          </div>
        )} */}
      </div>
    );
  };

  // Render form Level 1 section (BiPlusCircle is shown first in renderConfigForm, then this section)
  const renderFormLevel1Section = (hasLevel1, hasLevel2) => {
    return (
      <div className="mb-3 align-items-start">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <b>{t("ESGAnalytics.addTheLevel1Data.label")}</b>
        </div>
        <div className="d-flex align-items-center gap-2 mb-2">
          <label className='white-nowrap'>{t("ESGAnalytics.nameOfTheData.label")} <span className="text-danger">*</span></label>
          <Form.Control
            type="text"
            placeholder={t("ESGAnalytics.enterName.label")}
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            className="w-50"
            style={showValidationState && !formName.trim() ? { border: '2px solid var(--bs-danger)' } : undefined}
          />
        </div>
        <div className="d-flex align-items-center gap-2 mb-2">
          <label className='white-nowrap'>{t("ESGAnalytics.chooseTheDataFrom.label")} <span className="text-danger">*</span></label>
          <div className="d-flex align-items-center gap-3">
            <Form.Check
              label={t("Metric.label")}
              name="group1"
              type="radio"
              id="form-level1-metric-radio"
              checked={level1SelectionType === 'metric'}
              onChange={(e) => {
                if (e.target.checked) {
                  setLevel1SelectionType('metric');
                  setFormLevel1Equation(null);
                  setFormLevel1VerticalCalcEnabled(false);
                  setSelectedLevel1Datapoint(null);
                }
              }}
            />
            <Form.Check
              label={t("DataPoint.label")}
              name="group1"
              type="radio"
              id="form-level1-datapoint-radio"
              checked={level1SelectionType === 'datapoint'}
              onChange={(e) => {
                if (e.target.checked) {
                  setLevel1SelectionType('datapoint');
                  setFormLevel1Equation(null);
                  setFormLevel1VerticalCalcEnabled(false);
                  setSelectedLevel1Metric(null);
                }
              }}
            />
            {/* // Hidden  to reduce complexity
            <Form.Check
              label={t("Equation.label")}
              name="group1"
              type="radio"
              id="form-level1-equation-radio"
              checked={level1SelectionType === 'equation'}
              onChange={(e) => {
                if (e.target.checked) {
                  setLevel1SelectionType('equation');
                  setSelectedLevel1Metric(null);
                  setSelectedLevel1Datapoint(null);
                }
              }}
            /> */}
          </div>
          <div className="d-flex align-items-start gap-2 w-50">
            {level1SelectionType === 'metric' && (
              <div className={`position-relative w-75 ${showValidationState && !hasLevel1 ? 'rounded border border-2 border-danger' : ''}`} style={showValidationState && !hasLevel1 ? { padding: '2px' } : undefined}>
                {renderLevel1MetricTypeahead()}
              </div>
            )}
            {level1SelectionType === 'datapoint' && (
              <div className={`position-relative w-75 ${showValidationState && !hasLevel1 ? 'rounded border border-2 border-danger' : ''}`} style={showValidationState && !hasLevel1 ? { padding: '2px' } : undefined}>
                {renderLevel1DatapointTypeahead()}
              </div>
            )}
            {level1SelectionType === 'equation' && (
              <div className={showValidationState && !hasLevel1 ? 'rounded border border-2 border-danger' : ''} style={showValidationState && !hasLevel1 ? { padding: '2px' } : undefined}>
                <div className="d-flex align-items-center flex-wrap gap-2">
                  {renderEquationDisplay(
                    formLevel1Equation,
                    () => handleOpenEquation(null, null, 'formLevel1'),
                    openEquationIndex.configIndex === null
                      && openEquationIndex.rowId === null
                      && openEquationIndex.level === 'formLevel1'
                  )}
                  {hasEquationContent(formLevel1Equation) && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      type="button"
                      className="flex-shrink-0 align-self-center"
                      data-testid="esg-clear-form-level1-equation"
                      onClick={handleClearFormLevel1Equation}
                    >
                      {t("ESGAnalytics.clearEquation.button.label")}
                    </Button>
                  )}
                </div>
                <div className="mt-2 d-flex align-items-center">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="form-level1-vertical-calc-checkbox"
                    checked={formLevel1VerticalCalcEnabled}
                    onChange={(e) => setFormLevel1VerticalCalcEnabled(e.target.checked)}
                  />
                  <label className="form-check-label mx-1" htmlFor="form-level1-vertical-calc-checkbox">
                    {t("VerticalCalculationNeeded.label")}
                  </label>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="form-level1-vertical-calc-tooltip">{t("VerticalCalculationNeeded.tooltip.label")}</Tooltip>}
                  >
                    <span className="ms-1 d-inline-flex align-items-center" role="img" aria-label={t("VerticalCalculationNeeded.tooltip.label")}>
                      <BiInfoCircle size={16} className="text-muted" />
                    </span>
                  </OverlayTrigger>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 mb-2">
          <Form.Check
            type="checkbox"
            id="form-show-percentage-checkbox"
            checked={showPercentage}
            onChange={(e) => setShowPercentage(e.target.checked)}
            label={t("ESGAnalytics.showPercentageColumn.label")}
          />
        </div>
      </div>
    );
  };

  // Render form Level 2 row
  const renderFormLevel2Row = (row, showLevel2Validation = false) => {
    const rawLevel2SelectionType = row.level2SelectionType
      ? row.level2SelectionType
      : row.useEquation
        ? 'equation'
        : row.metric
          ? 'metric'
          : row.datapoint
            ? 'datapoint'
            : 'metric';
    // Level 2 equation UI removed — coerce legacy/stale equation rows so metric/datapoint controls stay usable
    const effectiveLevel2SelectionType = rawLevel2SelectionType === 'equation' ? 'metric' : rawLevel2SelectionType;
    const hasSelection = !!(row.metric || row.datapoint);

    const rowLabelInvalid = showLevel2Validation && !row.label.trim();
    const rowSelectionInvalid = showLevel2Validation && !(row.metric || row.datapoint);
    const selectedTypeForDrillDown = effectiveLevel2SelectionType;
    return (
      <div key={row.id} className="mb-2 p-2 LevelSelection_Card bg-opacity-50 rounded border">
        <div className="">
          <div className='d-flex align-items-center justify-content-between gap-2'>
            <div className='d-flex align-items-center gap-2 w-100'>
              <label>{t("ESGAnalytics.nameOfTheData.label")} <span className="text-danger">*</span></label>
              <Form.Control
                type="text"
                placeholder={t("ESGAnalytics.enterLabel.label")}
                value={row.label}
                onChange={(e) => handleLevel2RowLabelChange(row.id, e.target.value)}
                className="w-50"
                style={rowLabelInvalid ? { border: '2px solid var(--bs-danger)' } : undefined}
              />
            </div>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={`esg-remove-level2-row-tooltip-${row.id}`}>
                  {t("ESGAnalytics.removeLevel2Item.label")}
                </Tooltip>
              }
            >
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleRemoveLevel2Row(row.id)}
                className="d-flex align-items-center justify-content-center"
                style={{ padding: '8px' }}
              >
                <BiTrash size={16} />
              </Button>
            </OverlayTrigger>
          </div>
          <div className='d-flex align-items-center gap-2 mt-2'>
            <label>{t("ESGAnalytics.chooseTheDataFrom.label")}  </label>
            <div className='d-flex align-items-center gap-2'>
              <>
                <Form.Check
                  type="radio"
                  name={`form-level2-group-${row.id}`}
                  id={`form-level2-metric-radio-${row.id}`}
                  checked={effectiveLevel2SelectionType === 'metric'}
                  disabled={hasSelection && effectiveLevel2SelectionType !== 'metric'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const updatedRows = level2Rows.map(r =>
                        r.id === row.id
                          ? { ...r, useEquation: false, equation: null, datapoint: null, metric: null, level2SelectionType: 'metric' }
                          : r
                      );
                      setLevel2Rows(updatedRows);
                    }
                  }}
                  label={t("Metric.label")}
                />
                <Form.Check
                  type="radio"
                  name={`form-level2-group-${row.id}`}
                  id={`form-level2-datapoint-radio-${row.id}`}
                  checked={effectiveLevel2SelectionType === 'datapoint'}
                  disabled={hasSelection && effectiveLevel2SelectionType !== 'datapoint'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const updatedRows = level2Rows.map(r =>
                        r.id === row.id
                          ? { ...r, useEquation: false, equation: null, metric: null, datapoint: null, level2SelectionType: 'datapoint' }
                          : r
                      );
                      setLevel2Rows(updatedRows);
                    }
                  }}
                  label={t("DataPoint.label")}
                />
              </>
            </div>
            <div className={`w-50 ${rowSelectionInvalid ? 'rounded border border-2 border-danger' : ''}`} style={rowSelectionInvalid ? { padding: '2px' } : undefined}>
              {effectiveLevel2SelectionType === 'metric' && (
                <div className="position-relative w-75">
                  {renderFormLevel2RowMetricTypeahead(row)}
                </div>
              )}
              {effectiveLevel2SelectionType === 'datapoint' && (
                <div className="position-relative w-75">
                  {renderFormLevel2RowDatapointTypeahead(row)}
                </div>
              )}
            </div>
          </div>
          {selectedTypeForDrillDown !== 'datapoint' && <div className="mt-3 d-flex align-items-center gap-3">
            <div className="d-flex align-items-center">
              <input
                className="form-check-input"
                type="checkbox"
                id={`form-level2-drilldown-checkbox-${row.id}`}
                checked={row.drillDownEnabled || false}
                onChange={(e) => {
                  const updatedRows = level2Rows.map(r =>
                    r.id === row.id ? { ...r, drillDownEnabled: e.target.checked } : r
                  );
                  setLevel2Rows(updatedRows);
                }}
              />
              <label className="form-check-label mx-1" htmlFor={`form-level2-drilldown-checkbox-${row.id}`}>
                {t("ESGAnalytics.drillDown.label")}
              </label>
            </div>
            {row.drillDownEnabled && (
              <div className="d-flex align-items-center gap-3">
                <Form.Check
                  type="radio"
                  name={`form-level2-drilldown-${row.id}`}
                  id={`form-level2-drilldown-equation-${row.id}`}
                  checked={row.drillDownType === 'equation'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const updatedRows = level2Rows.map(r =>
                        r.id === row.id ? { ...r, drillDownType: 'equation' } : r
                      );
                      setLevel2Rows(updatedRows);
                    }
                  }}
                  label={t("ESGAnalytics.byEquation.label")}
                />
                {selectedTypeForDrillDown !== 'equation' && (
                  <Form.Check
                    type="radio"
                    name={`form-level2-drilldown-${row.id}`}
                    id={`form-level2-drilldown-metric-${row.id}`}
                    checked={row.drillDownType === 'metric'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const updatedRows = level2Rows.map(r =>
                          r.id === row.id ? { ...r, drillDownType: 'metric' } : r
                        );
                        setLevel2Rows(updatedRows);
                      }
                    }}
                    label={t("ESGAnalytics.byMetric.label")}
                  />
                )}
              </div>
            )}
          </div>}
        </div>
      </div>
    );
  };

  // Render form Level 2 section
  const renderFormLevel2Section = () => {
    const requiresLevel2 = level1SelectionType !== 'equation';
    const hasLevel2 = level2Rows.length > 0 && level2Rows.every(row => row.label.trim() && (row.metric || row.datapoint));
    const showLevel2Validation = showValidationState && requiresLevel2 && !hasLevel2;
    return (
      <div className="mt-3">
        <div className="d-flex gap-2 align-items-center mb-2">
          <small className={`fw-bold ${showLevel2Validation ? 'text-danger' : 'text-muted'}`}>{t("ESGAnalytics.level2Selection.label")} {requiresLevel2 && <span className="text-danger">*</span>}</small>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="esg-add-level2-row-tooltip">
                {t("ESGAnalytics.addLevel2Row.label")}
              </Tooltip>
            }
          >
            <Button
              variant="outline-primary "
              size="sm"
              onClick={handleAddLevel2Row}
              className="d-flex align-items-center justify-content-center"
              style={{ padding: '6px' }}
            >
              <BiPlusCircle size={18} />
            </Button>
          </OverlayTrigger>
        </div>
        {level2Rows.length === 0 ? (
          <div className={`text-muted text-center p-3 noLevel2Rows_card rounded ${showLevel2Validation ? 'border border-2 border-danger' : ''}`}>
            {t("ESGAnalytics.noLevel2Rows.label")}
          </div>
        ) : (
          level2Rows.map((row) => renderFormLevel2Row(row, showLevel2Validation))
        )}
      </div>
    );
  };

  // Config row Level 1 handlers
  const handleConfigRowLevel1MetricSelect = (index, metric) => {
    const updatedRows = [...configRows];
    if (!metric) {
      updatedRows[index].level1Metric = null;
      updatedRows[index].level1 = null;
      setConfigRows(updatedRows);
      return;
    }
    updatedRows[index].level1Metric = metric;
    updatedRows[index].level1Datapoint = null;
    updatedRows[index].level1 = { ...metric, itemType: 'metric' };
    updatedRows[index].level1SelectionType = 'metric';
    setConfigRows(updatedRows);
  };

  const handleConfigRowLevel1DatapointSelect = (index, datapoint) => {
    const updatedRows = [...configRows];
    if (!datapoint) {
      updatedRows[index].level1Datapoint = null;
      updatedRows[index].level1 = null;
      setConfigRows(updatedRows);
      return;
    }
    updatedRows[index].level1Datapoint = datapoint;
    updatedRows[index].level1Metric = null;
    updatedRows[index].level1 = { ...datapoint, itemType: 'datapoint' };
    updatedRows[index].level1SelectionType = 'datapoint';
    setConfigRows(updatedRows);
  };

  // Config row Level 2 handlers
  const initializeConfigLevel2Rows = (config) => {
    if (!config.level2Rows) {
      const level2Rows = [];
      if (config.level2Metrics && config.level2Metrics.length > 0) {
        config.level2Metrics.forEach(metric => {
          level2Rows.push({
            id: Date.now() + Math.random(),
            label: metric.label || '',
            metric: metric,
            datapoint: null,
            useEquation: false,
            equation: null,
            drillDownEnabled: false,
            drillDownType: 'equation'
          });
        });
      }
      if (config.level2Datapoints && config.level2Datapoints.length > 0) {
        config.level2Datapoints.forEach(datapoint => {
          level2Rows.push({
            id: Date.now() + Math.random(),
            label: datapoint.label || '',
            metric: null,
            datapoint: datapoint,
            useEquation: false,
            equation: null,
            drillDownEnabled: false,
            drillDownType: 'equation'
          });
        });
      }
      config.level2Rows = level2Rows;
    }
  };

  const createEmptyConfig = () => ({
    id: Date.now(),
    name: '',
    level1Metric: null,
    level1Datapoint: null,
    level2Rows: [],
    level2Metrics: [],
    level2Datapoints: [],
    level1: null,
    level2: [],
    useLevel1Equation: false,
    level1Equation: null,
    showPercentage: true
  });

  const handleAddEmptyConfigRow = () => {
    const newConfig = createEmptyConfig();
    setConfigRows(withSortOrders([...configRows, newConfig]));
    scrollToLastConfigRowRef.current = true;
  };

  // Insert a new config row at a specific index (after the clicked row)
  const handleInsertConfigAtIndex = (insertAfterIndex) => {
    const newConfig = createEmptyConfig();

    // Insert at insertAfterIndex + 1 (after the current row)
    const updatedRows = [...configRows];
    updatedRows.splice(insertAfterIndex + 1, 0, newConfig);
    setConfigRows(withSortOrders(updatedRows));
  };

  const handleDeleteConfigRowClick = (index) => {
    setDeleteConfigRowIndex(index);
    setShowDeleteConfigRowModal(true);
  };

  const handleDeleteConfigRowConfirm = () => {
    if (deleteConfigRowIndex === null) return;
    const updatedRows = withSortOrders(configRows.filter((_, i) => i !== deleteConfigRowIndex));
    setConfigRows(updatedRows);
    setShowDeleteConfigRowModal(false);
    setDeleteConfigRowIndex(null);
    // Persist change: reuse Save API only when updated rows pass validation (each row has Level 1 and Level 2)
    if (onSave && title.trim() && updatedRows.length > 0 && updatedRows.every(rowHasLevel1AndLevel2)) {
      onSave(title, updatedRows, filter, { stayOnPage: true });
    }
  };

  const handleDeleteConfigRowCancel = () => {
    setShowDeleteConfigRowModal(false);
    setDeleteConfigRowIndex(null);
  };

  const renderDeleteConfigRowModal = () => (
    <Modal
      show={showDeleteConfigRowModal}
      onHide={handleDeleteConfigRowCancel}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>{t("ESGAnalytics.deleteConfigRowConfirmation.label") || "Remove Level 1 Data"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {t("ESGAnalytics.deleteConfigRowConfirmationMessage.label") || "Are you sure you want to remove this item from the configuration? This action cannot be undone."}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleDeleteConfigRowCancel}>
          {t("ESGAnalytics.cancel.label") || "Cancel"}
        </Button>
        <Button variant="danger" onClick={handleDeleteConfigRowConfirm}>
          {t("ESGAnalytics.delete.label") || "Delete"}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const handleConfigRowAddLevel2Row = (index) => {
    const updatedRows = [...configRows];
    const config = updatedRows[index];
    const newRow = {
      id: Date.now(),
      label: '',
      metric: null,
      datapoint: null,
      useEquation: false,
      equation: null,
      drillDownEnabled: false,
      drillDownType: 'equation'
    };
    if (!config.level2Rows) {
      config.level2Rows = [];
    }
    config.level2Rows.push(newRow);
    setConfigRows(updatedRows);
  };

  const removeLevel2RowFromConfig = (configRowsData, configIndex, rowId) => {
    const updatedRows = [...configRowsData];
    const config = updatedRows[configIndex];
    if (!config.level2Rows) return updatedRows;
    config.level2Rows = config.level2Rows.filter(row => row.id !== rowId);
    config.level2Metrics = config.level2Rows.filter(row => row.metric).map(row => row.metric);
    config.level2Datapoints = config.level2Rows.filter(row => row.datapoint).map(row => row.datapoint);
    config.level2 = [
      ...config.level2Metrics.map(m => {
        const row = config.level2Rows.find(r => r.metric?.id === m.id);
        return {
          ...m,
          itemType: 'metric',
          label: row?.label || '',
          drillDownEnabled: row?.drillDownEnabled || false,
          drillDownType: row?.drillDownType || 'equation'
        };
      }),
      ...config.level2Datapoints.map(dp => {
        const row = config.level2Rows.find(r => r.datapoint?.id === dp.id);
        return {
          ...dp,
          itemType: 'datapoint',
          label: row?.label || '',
          drillDownEnabled: row?.drillDownEnabled || false,
          drillDownType: row?.drillDownType || 'equation'
        };
      })
    ];
    return updatedRows;
  };

  const handleDeleteLevel2RowClick = (configIndex, rowId) => {
    setDeleteLevel2ConfigIndex(configIndex);
    setDeleteLevel2RowId(rowId);
    setShowDeleteLevel2RowModal(true);
  };

  const handleDeleteLevel2RowConfirm = () => {
    if (deleteLevel2ConfigIndex === null || deleteLevel2RowId === null) return;
    const updatedRows = removeLevel2RowFromConfig(configRows, deleteLevel2ConfigIndex, deleteLevel2RowId);
    setConfigRows(updatedRows);
    setShowDeleteLevel2RowModal(false);
    setDeleteLevel2ConfigIndex(null);
    setDeleteLevel2RowId(null);
    // Persist change: reuse Save API only when updated rows pass validation (each row has Level 1 and Level 2)
    if (onSave && title.trim() && updatedRows.length > 0 && updatedRows.every(rowHasLevel1AndLevel2)) {
      onSave(title, updatedRows, filter, { stayOnPage: true });
    }
  };

  const handleDeleteLevel2RowCancel = () => {
    setShowDeleteLevel2RowModal(false);
    setDeleteLevel2ConfigIndex(null);
    setDeleteLevel2RowId(null);
  };

  const renderDeleteLevel2RowModal = () => (
    <Modal
      show={showDeleteLevel2RowModal}
      onHide={handleDeleteLevel2RowCancel}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>{t("ESGAnalytics.deleteLevel2RowConfirmation.label") || "Remove Level 2 Item"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {t("ESGAnalytics.deleteLevel2RowConfirmationMessage.label") || "Are you sure you want to remove this Level 2 item from the configuration? This action cannot be undone."}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleDeleteLevel2RowCancel}>
          {t("ESGAnalytics.cancel.label") || "Cancel"}
        </Button>
        <Button variant="danger" onClick={handleDeleteLevel2RowConfirm}>
          {t("ESGAnalytics.delete.label") || "Delete"}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const handleConfigRowRemoveLevel2Row = (index, rowId) => {
    const updatedRows = removeLevel2RowFromConfig(configRows, index, rowId);
    setConfigRows(updatedRows);
  };

  const handleConfigRowLevel2RowLabelChange = (index, rowId, label) => {
    const updatedRows = [...configRows];
    updatedRows[index].level2Rows = updatedRows[index].level2Rows.map(row =>
      row.id === rowId ? { ...row, label } : row
    );
    updatedRows[index].level2 = [
      ...updatedRows[index].level2Rows.filter(row => row.metric).map(row => ({
        ...row.metric,
        itemType: 'metric',
        label: row.label,
        drillDownEnabled: row.drillDownEnabled || false,
        drillDownType: row.drillDownType || 'equation'
      })),
      ...updatedRows[index].level2Rows.filter(row => row.datapoint).map(row => ({
        ...row.datapoint,
        itemType: 'datapoint',
        label: row.label,
        drillDownEnabled: row.drillDownEnabled || false,
        drillDownType: row.drillDownType || 'equation'
      }))
    ];
    setConfigRows(updatedRows);
  };

  const handleConfigRowLevel2RowMetricSelect = (index, rowId, metric) => {
    const updatedRows = [...configRows];
    updatedRows[index].level2Rows = updatedRows[index].level2Rows.map(row => {
      if (row.id !== rowId) return row;
      if (!metric) {
        return { ...row, metric: null };
      }
      const eqExpression = metric?.eqExpression ?? null;
      const eqExpressionWithId = metric?.eqExpressionWithId ?? null;
      const metricWithEq = {
        ...metric,
        eqExpression,
        eqExpressionWithId
      };
      return {
        ...row,
        metric: metricWithEq,
        datapoint: null,
        level2SelectionType: 'metric'
      };
    });
    updatedRows[index].level2Metrics = updatedRows[index].level2Rows.filter(row => row.metric).map(row => row.metric);
    updatedRows[index].level2Datapoints = updatedRows[index].level2Rows.filter(row => row.datapoint).map(row => row.datapoint);
    updatedRows[index].level2 = [
      ...updatedRows[index].level2Rows.filter(row => row.metric).map(row => {
        const m = row.metric || {};
        const eqExpression = m?.eqExpression ?? null;
        const eqExpressionWithId = m?.eqExpressionWithId ?? null;
        return {
          ...m,
          eqExpression,
          eqExpressionWithId,
          itemType: 'metric',
          label: row.label,
          drillDownEnabled: row.drillDownEnabled || false,
          drillDownType: row.drillDownType || 'equation'
        };
      }),
      ...updatedRows[index].level2Rows.filter(row => row.datapoint).map(row => ({
        ...row.datapoint,
        itemType: 'datapoint',
        label: row.label,
        drillDownEnabled: row.drillDownEnabled || false,
        drillDownType: row.drillDownType || 'equation'
      }))
    ];
    setConfigRows(updatedRows);
  };

  const handleConfigRowLevel2RowDatapointSelect = (index, rowId, datapoint) => {
    const updatedRows = [...configRows];
    updatedRows[index].level2Rows = updatedRows[index].level2Rows.map(row => {
      if (row.id !== rowId) return row;
      if (!datapoint) {
        return { ...row, datapoint: null };
      }
      return { ...row, datapoint, metric: null, level2SelectionType: 'datapoint' };
    });
    updatedRows[index].level2Metrics = updatedRows[index].level2Rows.filter(row => row.metric).map(row => row.metric);
    updatedRows[index].level2Datapoints = updatedRows[index].level2Rows.filter(row => row.datapoint).map(row => row.datapoint);
    updatedRows[index].level2 = [
      ...updatedRows[index].level2Rows.filter(row => row.metric).map(row => ({
        ...row.metric,
        itemType: 'metric',
        label: row.label,
        drillDownEnabled: row.drillDownEnabled || false,
        drillDownType: row.drillDownType || 'equation'
      })),
      ...updatedRows[index].level2Rows.filter(row => row.datapoint).map(row => ({
        ...row.datapoint,
        itemType: 'datapoint',
        label: row.label,
        drillDownEnabled: row.drillDownEnabled || false,
        drillDownType: row.drillDownType || 'equation'
      }))
    ];
    setConfigRows(updatedRows);
  };

  const renderConfigRowLevel2RowMetricTypeahead = (index, row) => {
    const config = configRows[index];
    return (
      <Typeahead
        id={`config-${config.id}-l2-metric-${row.id}`}
        className="w-100"
        inputProps={{ className: 'ghg-dropdown-toggle text-truncate', style: { minWidth: '200px' } }}
        labelKey={metricDatapointTypeaheadLabel}
        options={allMetrics}
        placeholder={t("ESGAnalytics.selectMetric.label")}
        selected={row.metric ? [row.metric] : []}
        onChange={(sel) => {
          if (sel.length > 0) handleConfigRowLevel2RowMetricSelect(index, row.id, sel[0]);
          else handleConfigRowLevel2RowMetricSelect(index, row.id, null);
        }}
        clearButton
        disabled={!!row.datapoint}
      />
    );
  };

  const renderConfigRowLevel2RowDatapointTypeahead = (index, row) => {
    const config = configRows[index];
    return (
      <Typeahead
        id={`config-${config.id}-l2-datapoint-${row.id}`}
        className="w-100"
        inputProps={{ className: 'ghg-dropdown-toggle text-truncate', style: { minWidth: '200px' } }}
        labelKey={metricDatapointTypeaheadLabel}
        options={allDatapoints}
        placeholder={t("ESGAnalytics.selectDatapoint.label")}
        selected={row.datapoint ? [row.datapoint] : []}
        onChange={(sel) => {
          if (sel.length > 0) handleConfigRowLevel2RowDatapointSelect(index, row.id, sel[0]);
          else handleConfigRowLevel2RowDatapointSelect(index, row.id, null);
        }}
        clearButton
        disabled={!!row.metric}
      />
    );
  };

  const renderConfigLevel2Row = (index, row, isRowInvalid = false) => {
    const rowLabelInvalid = isRowInvalid && !(row.label && row.label.trim());
    const rowSelectionInvalid = isRowInvalid && !(row.metric || row.datapoint || (row.useEquation && row.equation && (row.equation.expressionWithId || row.equation.expression)));
    const selectedTypeForDrillDown = row.level2SelectionType
      ? row.level2SelectionType
      : row.useEquation
        ? 'equation'
        : row.metric
          ? 'metric'
          : row.datapoint
            ? 'datapoint'
            : 'metric';
    return (
      <div key={row.id} className="mb-2 p-2 LevelSelection_Card bg-opacity-50 rounded border">
        <div className="">
          <div className='d-flex align-items-center justify-content-between gap-2'>
            <div className='d-flex align-items-center gap-2 w-100'>
              <label>{t("ESGAnalytics.nameOfTheData.label")} <span className="text-danger">*</span></label>
              <Form.Control
                type="text"
                className="w-50"
                placeholder={t("ESGAnalytics.enterLabel.label")}
                value={row.label || ''}
                onChange={(e) => handleConfigRowLevel2RowLabelChange(index, row.id, e.target.value)}
                style={rowLabelInvalid ? { border: '2px solid var(--bs-danger)' } : undefined}
              />
            </div>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={`esg-remove-level2-config-tooltip-${index}-${row.id}`}>
                  {t("ESGAnalytics.removeLevel2Item.label")}
                </Tooltip>
              }
            >
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleDeleteLevel2RowClick(index, row.id)}
                className="d-flex align-items-center justify-content-center"
                style={{ padding: '6px' }}
              >
                <BiTrash size={16} />
              </Button>
            </OverlayTrigger>
          </div>
          <div className='d-flex align-items-center gap-2 mt-2'>
            <label>{t("ESGAnalytics.chooseTheDataFrom.label")}  </label>
            <div className='d-flex align-items-center gap-2'>
              {(() => {
                // Determine current selection type
                const currentSelectionType = row.level2SelectionType
                  ? row.level2SelectionType
                  : row.useEquation
                    ? 'equation'
                    : row.metric
                      ? 'metric'
                      : row.datapoint
                        ? 'datapoint'
                        : 'metric'; // default

                // Check if a selection has been made (disable unchecked radio buttons if value is selected)
                const hasSelection = row.metric || row.datapoint || (row.useEquation && row.equation);

                return (
                  <>
                    <Form.Check
                      type="radio"
                      name={`level2-group-${row.id}`}
                      id={`level2-metric-radio-${row.id}`}
                      checked={currentSelectionType === 'metric'}
                      disabled={hasSelection && currentSelectionType !== 'metric'}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const updatedRows = [...configRows];
                          const rowIndex = updatedRows[index].level2Rows.findIndex(r => r.id === row.id);
                          if (rowIndex >= 0) {
                            updatedRows[index].level2Rows[rowIndex].useEquation = false;
                            updatedRows[index].level2Rows[rowIndex].equation = null;
                            updatedRows[index].level2Rows[rowIndex].datapoint = null;
                            updatedRows[index].level2Rows[rowIndex].metric = null;
                            updatedRows[index].level2Rows[rowIndex].level2SelectionType = 'metric';
                          }
                          setConfigRows(updatedRows);
                        }
                      }}
                      label={t("Metric.label")}
                    />
                    <Form.Check
                      type="radio"
                      name={`level2-group-${row.id}`}
                      id={`level2-datapoint-radio-${row.id}`}
                      checked={currentSelectionType === 'datapoint'}
                      disabled={hasSelection && currentSelectionType !== 'datapoint'}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const updatedRows = [...configRows];
                          const rowIndex = updatedRows[index].level2Rows.findIndex(r => r.id === row.id);
                          if (rowIndex >= 0) {
                            updatedRows[index].level2Rows[rowIndex].useEquation = false;
                            updatedRows[index].level2Rows[rowIndex].equation = null;
                            updatedRows[index].level2Rows[rowIndex].metric = null;
                            updatedRows[index].level2Rows[rowIndex].datapoint = null;
                            updatedRows[index].level2Rows[rowIndex].level2SelectionType = 'datapoint';
                          }
                          setConfigRows(updatedRows);
                        }
                      }}
                      label={t("DataPoint.label")}
                    />
                    <Form.Check
                      type="radio"
                      name={`level2-group-${row.id}`}
                      id={`level2-equation-radio-${row.id}`}
                      checked={currentSelectionType === 'equation'}
                      disabled={hasSelection && currentSelectionType !== 'equation'}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const updatedRows = [...configRows];
                          const rowIndex = updatedRows[index].level2Rows.findIndex(r => r.id === row.id);
                          if (rowIndex >= 0) {
                            updatedRows[index].level2Rows[rowIndex].useEquation = true;
                            updatedRows[index].level2Rows[rowIndex].metric = null;
                            updatedRows[index].level2Rows[rowIndex].datapoint = null;
                            updatedRows[index].level2Rows[rowIndex].level2SelectionType = 'equation';
                            if (!updatedRows[index].level2Rows[rowIndex].equation) {
                              updatedRows[index].level2Rows[rowIndex].equation = null;
                            }
                          }
                          setConfigRows(updatedRows);
                        }
                      }}
                      label={t("Equation.label")}
                    />
                  </>
                );
              })()}
            </div>
            <div className={`w-50 ${rowSelectionInvalid ? 'rounded border border-2 border-danger' : ''}`} style={rowSelectionInvalid ? { padding: '2px' } : undefined}>
              {(() => {
                // Determine current selection type
                const currentSelectionType = row.level2SelectionType
                  ? row.level2SelectionType
                  : row.useEquation
                    ? 'equation'
                    : row.metric
                      ? 'metric'
                      : row.datapoint
                        ? 'datapoint'
                        : 'metric'; // default

                // Show only the selected section
                if (currentSelectionType === 'metric') {
                  return (
                    <div className="position-relative w-75">
                      {renderConfigRowLevel2RowMetricTypeahead(index, row)}
                    </div>
                  );
                } else if (currentSelectionType === 'datapoint') {
                  return (
                    <div className="position-relative w-75">
                      {renderConfigRowLevel2RowDatapointTypeahead(index, row)}
                    </div>
                  );
                } else if (currentSelectionType === 'equation') {
                  return (
                    <div className="w-100">
                      <div className="d-flex align-items-center flex-wrap gap-2">
                        {renderEquationDisplay(
                          row.equation,
                          () => handleOpenEquation(index, row.id, 'level2'),
                          openEquationIndex.configIndex === index && openEquationIndex.rowId === row.id && openEquationIndex.level === 'level2'
                        )}
                        {hasEquationContent(row.equation) && (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            type="button"
                            className="flex-shrink-0 align-self-center"
                            data-testid={`esg-clear-config-level2-equation-${row.id}`}
                            onClick={() => handleClearConfigLevel2Equation(index, row.id)}
                          >
                            {t("ESGAnalytics.clearEquation.button.label")}
                          </Button>
                        )}
                      </div>
                      {row.equation && (
                        <div className="mt-2 d-flex align-items-center">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`config-level2-vertical-calc-checkbox-${row.id}`}
                            checked={row.verticalCalcEnabled || false}
                            onChange={(e) => {
                              const updatedRows = [...configRows];
                              const rowIndex = updatedRows[index].level2Rows.findIndex(r => r.id === row.id);
                              if (rowIndex >= 0) {
                                updatedRows[index].level2Rows[rowIndex].verticalCalcEnabled = e.target.checked;
                              }
                              setConfigRows(updatedRows);
                            }}
                          />
                          <label className="form-check-label mx-1" htmlFor={`config-level2-vertical-calc-checkbox-${row.id}`}>
                            {t("VerticalCalculationNeeded.label")}
                          </label>
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id={`config-level2-vertical-calc-tooltip-${row.id}`}>{t("VerticalCalculationNeeded.tooltip.label")}</Tooltip>}
                          >
                            <span className="ms-1 d-inline-flex align-items-center" role="img" aria-label={t("VerticalCalculationNeeded.tooltip.label")}>
                              <BiInfoCircle size={16} className="text-muted" />
                            </span>
                          </OverlayTrigger>
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
          {selectedTypeForDrillDown !== 'datapoint' && <div className="mt-3 d-flex align-items-center gap-3">
            <div className="d-flex align-items-center">
              <input
                className="form-check-input"
                type="checkbox"
                id={`config-level2-drilldown-checkbox-${row.id}`}
                checked={row.drillDownEnabled || false}
                onChange={(e) => {
                  const updatedRows = [...configRows];
                  const rowIndex = updatedRows[index].level2Rows.findIndex(r => r.id === row.id);
                  if (rowIndex >= 0) {
                    updatedRows[index].level2Rows[rowIndex].drillDownEnabled = e.target.checked;
                  }
                  setConfigRows(updatedRows);
                }}
              />
              <label className="form-check-label mx-1" htmlFor={`config-level2-drilldown-checkbox-${row.id}`}>
                {t("ESGAnalytics.drillDown.label")}
              </label>
            </div>
            {row.drillDownEnabled && (
              <div className="d-flex align-items-center gap-3">
                <Form.Check
                  type="radio"
                  name={`config-level2-drilldown-${row.id}`}
                  id={`config-level2-drilldown-equation-${row.id}`}
                  checked={row.drillDownType === 'equation'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const updatedRows = [...configRows];
                      const rowIndex = updatedRows[index].level2Rows.findIndex(r => r.id === row.id);
                      if (rowIndex >= 0) {
                        updatedRows[index].level2Rows[rowIndex].drillDownType = 'equation';
                      }
                      setConfigRows(updatedRows);
                    }
                  }}
                  label={t("ESGAnalytics.byEquation.label")}
                />
                {selectedTypeForDrillDown !== 'equation' && (
                  <Form.Check
                    type="radio"
                    name={`config-level2-drilldown-${row.id}`}
                    id={`config-level2-drilldown-metric-${row.id}`}
                    checked={row.drillDownType === 'metric'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const updatedRows = [...configRows];
                        const rowIndex = updatedRows[index].level2Rows.findIndex(r => r.id === row.id);
                        if (rowIndex >= 0) {
                          updatedRows[index].level2Rows[rowIndex].drillDownType = 'metric';
                        }
                        setConfigRows(updatedRows);
                      }
                    }}
                    label={t("ESGAnalytics.byMetric.label")}
                  />
                )}
              </div>
            )}
          </div>}
          <div className="col-md-1 d-flex align-items-center justify-content-end align-self-center">

          </div>
        </div>
      </div>
    );
  };

  // Render config row Level 1 section
  const renderConfigRowLevel1 = (config, index, isRowInvalid = false) => {
    const hasName = !!(config.name && typeof config.name === 'string' && config.name.trim());
    const hasLevel1 = !!(config.useLevel1Equation && config.level1Equation && (config.level1Equation.expressionWithId || config.level1Equation.expression)) || !!(config.level1 && config.level1.id);
    const level1ControlInvalid = isRowInvalid && !hasLevel1;
    const nameControlInvalid = isRowInvalid && !hasName;

    return (
      <div className="mb-3 align-items-start">
        <div className="d-flex align-items-center gap-2 mb-2">
          <b>{t("ESGAnalytics.addTheLevel1Data.label")}</b>
          <div className="d-flex align-items-center gap-2">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id={`insert-config-tooltip-${index}`}>{t("ESGAnalytics.insertNewConfigBelow.label")}</Tooltip>}
            >
              <Button
                variant="outline-primary"
                onClick={() => handleInsertConfigAtIndex(index)}
                className="d-flex align-items-center justify-content-center"
                size="sm"
                style={{ padding: '6px' }}
              >
                <BiPlusCircle size={18} />
              </Button>
            </OverlayTrigger>
            {renderConfigRowMoveControls(index)}
            <Button
              variant="danger"
              size="sm"
              onClick={() => handleDeleteConfigRowClick(index)}
              style={{ padding: '6px 8px' }}
              title={t("ESGAnalytics.delete.label")}
            >
              <BiTrash size={16} />
            </Button>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 mb-2">
          <label className='white-nowrap'>{t("ESGAnalytics.nameOfTheData.label")} <span className="text-danger">*</span></label>
          <Form.Control
            type="text"
            placeholder={t("ESGAnalytics.enterName.label")}
            value={config.name}
            className="w-50"
            style={nameControlInvalid ? { border: '2px solid var(--bs-danger)' } : undefined}
            onChange={(e) => {
              const updatedRows = [...configRows];
              updatedRows[index].name = e.target.value;
              setConfigRows(updatedRows);
            }}
          />
        </div>
        <div className="d-flex align-items-center gap-2 mb-2">
          <label className='white-nowrap'>{t("ESGAnalytics.chooseTheDataFrom.label")}</label>
          <div className="d-flex align-items-center gap-2">
            {(() => {
              // Determine current selection type
              const currentSelectionType = config.level1SelectionType
                ? config.level1SelectionType
                : config.useLevel1Equation
                  ? 'equation'
                  : config.level1Metric
                    ? 'metric'
                    : config.level1Datapoint
                      ? 'datapoint'
                      : 'metric'; // default

              // Check if a selection has been made (disable unchecked radio buttons if value is selected)
              const hasSelection = config.level1Metric || config.level1Datapoint || (config.useLevel1Equation && config.level1Equation);

              return (
                <>
                  <Form.Check
                    label={t("Metric.label")}
                    name={`group1-${config.id}`}
                    type="radio"
                    id={`config-level1-metric-radio-${config.id}`}
                    checked={currentSelectionType === 'metric'}
                    disabled={hasSelection && currentSelectionType !== 'metric'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const updatedRows = [...configRows];
                        updatedRows[index].useLevel1Equation = false;
                        updatedRows[index].level1Equation = null;
                        updatedRows[index].level1Datapoint = null;
                        updatedRows[index].level1Metric = null;
                        updatedRows[index].level1 = null;
                        updatedRows[index].level1SelectionType = 'metric';
                        setConfigRows(updatedRows);
                      }
                    }}
                  />
                  <Form.Check
                    label={t("DataPoint.label")}
                    name={`group1-${config.id}`}
                    type="radio"
                    id={`config-level1-datapoint-radio-${config.id}`}
                    checked={currentSelectionType === 'datapoint'}
                    disabled={hasSelection && currentSelectionType !== 'datapoint'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const updatedRows = [...configRows];
                        updatedRows[index].useLevel1Equation = false;
                        updatedRows[index].level1Equation = null;
                        updatedRows[index].level1Metric = null;
                        updatedRows[index].level1Datapoint = null;
                        updatedRows[index].level1 = null;
                        updatedRows[index].level1SelectionType = 'datapoint';
                        setConfigRows(updatedRows);
                      }
                    }}
                  />
                  <Form.Check
                    label={t("Equation.label")}
                    name={`group1-${config.id}`}
                    type="radio"
                    id={`config-level1-equation-radio-${config.id}`}
                    checked={currentSelectionType === 'equation'}
                    disabled={hasSelection && currentSelectionType !== 'equation'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const updatedRows = [...configRows];
                        updatedRows[index].useLevel1Equation = true;
                        updatedRows[index].level1Metric = null;
                        updatedRows[index].level1Datapoint = null;
                        updatedRows[index].level1 = null;
                        updatedRows[index].level1SelectionType = 'equation';
                        if (!updatedRows[index].level1Equation) {
                          updatedRows[index].level1Equation = null;
                        }
                        setConfigRows(updatedRows);
                      }
                    }}
                  />
                </>
              );
            })()}
          </div>
          <div className={`w-50 d-flex align-items-start gap-2 ${level1ControlInvalid ? 'rounded border border-2 border-danger' : ''}`} style={level1ControlInvalid ? { padding: '2px' } : undefined}>
            {(() => {
              // Determine current selection type - check level1SelectionType first, then fallback to existing logic
              const currentSelectionType = config.level1SelectionType
                ? config.level1SelectionType
                : config.useLevel1Equation
                  ? 'equation'
                  : config.level1Metric
                    ? 'metric'
                    : config.level1Datapoint
                      ? 'datapoint'
                      : 'metric'; // default

              // Show only the selected section
              if (currentSelectionType === 'metric') {
                return (
                  <div className="w-75">
                    <Typeahead
                      id={`config-row-level1-metric-${config.id}`}
                      className="w-100"
                      inputProps={{ className: 'ghg-dropdown-toggle text-truncate', style: { minWidth: '200px' } }}
                      labelKey={metricDatapointTypeaheadLabel}
                      options={allMetrics}
                      placeholder={t("ESGAnalytics.selectMetric.label")}
                      selected={config.level1Metric ? [config.level1Metric] : []}
                      onChange={(sel) => {
                        if (sel.length > 0) handleConfigRowLevel1MetricSelect(index, sel[0]);
                        else handleConfigRowLevel1MetricSelect(index, null);
                      }}
                      clearButton
                      disabled={!!config.level1Datapoint}
                    />
                  </div>
                );
              } else if (currentSelectionType === 'datapoint') {
                return (
                  <div className="w-75">
                    <Typeahead
                      id={`config-row-level1-datapoint-${config.id}`}
                      className="w-100"
                      inputProps={{ className: 'ghg-dropdown-toggle text-truncate', style: { minWidth: '200px' } }}
                      labelKey={metricDatapointTypeaheadLabel}
                      options={allDatapoints}
                      placeholder={t("ESGAnalytics.selectDatapoint.label")}
                      selected={config.level1Datapoint ? [config.level1Datapoint] : []}
                      onChange={(sel) => {
                        if (sel.length > 0) handleConfigRowLevel1DatapointSelect(index, sel[0]);
                        else handleConfigRowLevel1DatapointSelect(index, null);
                      }}
                      clearButton
                      disabled={!!config.level1Metric}
                    />
                  </div>
                );
              } else if (currentSelectionType === 'equation') {
                return (
                  <div className="w-100">
                    <div className="d-flex align-items-center flex-wrap gap-2">
                      {renderEquationDisplay(
                        config.level1Equation,
                        () => handleOpenEquation(index, null, 'level1'),
                        openEquationIndex.configIndex === index
                          && openEquationIndex.rowId === null
                          && openEquationIndex.level === 'level1'
                      )}
                      {hasEquationContent(config.level1Equation) && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          type="button"
                          className="flex-shrink-0 align-self-center"
                          data-testid={`esg-clear-config-level1-equation-${config.id}`}
                          onClick={() => handleClearConfigLevel1Equation(index, config.id)}
                        >
                          {t("ESGAnalytics.clearEquation.button.label")}
                        </Button>
                      )}
                    </div>
                    {config.level1Equation && (
                      <div className="mt-2 d-flex align-items-center">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`config-level1-vertical-calc-checkbox-${config.id}`}
                          checked={config.level1VerticalCalcEnabled || false}
                          onChange={(e) => {
                            const updatedRows = [...configRows];
                            updatedRows[index].level1VerticalCalcEnabled = e.target.checked;
                            setConfigRows(updatedRows);
                          }}
                        />
                        <label className="form-check-label mx-1" htmlFor={`config-level1-vertical-calc-checkbox-${config.id}`}>
                          {t("VerticalCalculationNeeded.label")}
                        </label>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id={`config-level1-vertical-calc-tooltip-${config.id}`}>{t("VerticalCalculationNeeded.tooltip.label")}</Tooltip>}
                        >
                          <span className="ms-1 d-inline-flex align-items-center" role="img" aria-label={t("VerticalCalculationNeeded.tooltip.label")}>
                            <BiInfoCircle size={16} className="text-muted" />
                          </span>
                        </OverlayTrigger>
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            })()}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 mb-2">
          <Form.Check
            type="checkbox"
            id={`config-show-percentage-checkbox-${config.id}`}
            checked={config.showPercentage !== false} // Default to true if not set
            onChange={(e) => {
              const updatedRows = [...configRows];
              updatedRows[index].showPercentage = e.target.checked;
              setConfigRows(updatedRows);
            }}
            label={t("ESGAnalytics.showPercentageColumn.label")}
          />
        </div>
      </div>
    );
  };

  // Render config row Level 2 section
  const renderConfigRowLevel2 = (config, index, isRowInvalid = false) => {
    // Initialize level2Rows if not present
    initializeConfigLevel2Rows(config);
    const hasLevel2 = config.level2Rows && config.level2Rows.length > 0 && config.level2Rows.every(r =>
      (r.label && r.label.trim()) &&
      (r.metric || r.datapoint || (r.useEquation && r.equation && (r.equation.expressionWithId || r.equation.expression)))
    );
    const level2SectionInvalid = isRowInvalid && !hasLevel2;

    return (
      <div className="mt-3">
        <div className="d-flex gap-2 align-items-center mb-2">
          <small className="text-muted fw-bold">{t("ESGAnalytics.level2Selection.label")}</small>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`esg-add-level2-row-config-tooltip-${index}`}>
                {t("ESGAnalytics.addLevel2Row.label")}
              </Tooltip>
            }
          >
            <Button
              variant="outline-primary "
              size="sm"
              onClick={() => handleConfigRowAddLevel2Row(index)}
              className="d-flex align-items-center justify-content-center"
              style={{ padding: '6px' }}
            >
              <BiPlusCircle size={18} />
            </Button>
          </OverlayTrigger>
        </div>
        {(!config.level2Rows || config.level2Rows.length === 0) ? (
          <div className={`text-muted text-center p-3 bg-light rounded ${level2SectionInvalid ? 'border border-2 border-danger' : ''}`}>
            {t("ESGAnalytics.noLevel2Rows.label")}
          </div>
        ) : (
          config.level2Rows.map((row) => renderConfigLevel2Row(index, row, isRowInvalid))
        )}
      </div>
    );
  };

  // Scroll to last config row after it's mounted (e.g. when user clicked "Add New Level1 Data" in Edit)
  useEffect(() => {
    if (scrollToLastConfigRowRef.current && lastConfigRowRef.current) {
      lastConfigRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      scrollToLastConfigRowRef.current = false;
    }
  }, [configRows.length]);

  // Render saved config rows
  const renderConfigRows = () => {
    if (configRows.length === 0) return null;

    return (
      <div className="mt-3">
        {configRows.map((config, index) => {
          const isRowInvalid = showValidationState && !rowHasLevel1AndLevel2(config);
          const isLastRow = index === configRows.length - 1;
          return (
            <div
              key={config.id}
              ref={isLastRow ? lastConfigRowRef : null}
              className="mt-2 p-3 Unit_Wise_Card bg-opacity-90 border 2"
            >
              {renderConfigRowLevel1(config, index, isRowInvalid)}
              {renderConfigRowLevel2(config, index, isRowInvalid)}
            </div>
          );
        })}
      </div>
    );
  };

  const renderConfigForm = () => {
    const hasLevel1 = level1SelectionType === 'equation'
      ? formLevel1Equation?.expression
      : level1SelectionType === 'metric'
        ? selectedLevel1Metric
        : level1SelectionType === 'datapoint'
          ? selectedLevel1Datapoint
          : false;
    const hasLevel2 = level2Rows.length > 0 && level2Rows.every(row => row.label.trim() && (row.metric || row.datapoint));

    return (
      <>
        {renderConfigFormTitle()}
        {(editMode && simplifiedEditForm) ? (
          <div className="mt-3 d-flex justify-content-end">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="insert-config-tooltip">{t("ESGAnalytics.insertNewConfigBelow.label")}</Tooltip>}
            >
              <Button
                variant="outline-primary"
                onClick={handleAddEmptyConfigRow}
                className="d-flex align-items-center justify-content-center gap-2"
                size="sm"
                style={{ padding: '6px' }}
              >
                {t("ESGAnalytics.insertNewConfigBelow.label")}
                <BiPlusCircle size={18} />
              </Button>
            </OverlayTrigger>
          </div>
        ) : (
          <div className="mt-3 p-3 Unit_Wise_Card bg-opacity-80 3">
            {renderFormLevel1Section(hasLevel1, hasLevel2)}
            {renderFormLevel2Section()}
            <div className="mb-3 d-flex justify-content-end">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id="insert-config-tooltip">{t("ESGAnalytics.insertNewConfigBelow.label")}</Tooltip>}
              >
                <Button
                  variant="outline-primary"
                  onClick={handleInsertConfig}
                  disabled={!formName.trim() || !hasLevel1 || (level1SelectionType !== 'equation' && !hasLevel2)}
                  className="d-flex align-items-center justify-content-center gap-1"
                  size="sm"
                  style={{ padding: '6px 12px' }}
                >
                  <BiPlusCircle size={18} />
                </Button>
              </OverlayTrigger>
            </div>
          </div>
        )}
        {renderConfigRows()}
      </>
    );
  };

  const renderEntityTreeView = () => {
    if (editMode) return null;

    return (
      <div className="esg-entity-tree-container">
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0 fw-semibold d-flex align-items-center gap-1">
              {t("ESGAnalytics.orgHierarchy.label")}
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="esg-org-hierarchy-tooltip">
                    {t("ESGAnalytics.showsParentAndChildEntity.label")}
                  </Tooltip>
                }
              >
                <span className="esg-analytics-frequency-info-icon" aria-label={t("ESGAnalytics.showsParentAndChildEntity.label")}>
                  <BiInfoCircle size={14} />
                </span>
              </OverlayTrigger>
            </h6>
          </div>
          {enterpriseStructure.length > 0 && (
            <div className="d-flex flex-wrap align-items-center esg-entity-tree-root-bulk">
              <button
                type="button"
                className="btn btn-sm btn-primary ghg-select-all-btn"
                onClick={handleTreeRootSelectAll}
                disabled={areAllRootTreeEntitiesSelected()}
              >
                {t('SelectAll.label')}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary ghg-unselect-all-btn"
                onClick={handleTreeRootUnselectAll}
                disabled={areNoRootTreeEntitiesSelected()}
              >
                {t('UnselectAll.label')}
              </button>
            </div>
          )}
        </div>
        <div className="esg-entity-tree-wrapper">
          {enterpriseStructure.length > 0 ? (
            renderEntityTree(enterpriseStructure)
          ) : (
            <div className="text-muted text-center py-3">
              {t("Loading.label")}
            </div>
          )}
        </div>
      </div>
    );
  };

  const moveConfigRow = (index, direction) => {
    const nextIndex = direction === 'up' ? index - 1 : index + 1;
    if (nextIndex < 0 || nextIndex >= configRows.length) {
      return;
    }
    const rows = [...configRows];
    const temp = rows[index];
    rows[index] = rows[nextIndex];
    rows[nextIndex] = temp;
    setConfigRows(withSortOrders(rows));
  };

  const renderConfigRowMoveControls = (index) => {
    if (configRows.length <= 1) {
      return null;
    }

    return (
      <>
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id={`esg-config-move-up-${index}`}>{t("MoveUp.label")}</Tooltip>}
        >
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={index === 0}
            onClick={() => moveConfigRow(index, 'up')}
            className="d-flex align-items-center justify-content-center"
            aria-label={t("MoveUp.label")}
            style={{ padding: '6px 8px' }}
          >
            <BiUpArrow size={16} />
          </Button>
        </OverlayTrigger>
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id={`esg-config-move-down-${index}`}>{t("MoveDown.label")}</Tooltip>}
        >
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={index === configRows.length - 1}
            onClick={() => moveConfigRow(index, 'down')}
            className="d-flex align-items-center justify-content-center"
            aria-label={t("MoveDown.label")}
            style={{ padding: '6px 8px' }}
          >
            <BiDownArrow size={16} />
          </Button>
        </OverlayTrigger>
      </>
    );
  };

  return (
    <div className={`ghg-emission-header 1 ${!editMode ? 'esg-analytics-header-sidebar' : ''}`} data-testid="esg-unit-wise-config-form">
      <div className={`ghg-header-filters-row 1 ${!editMode ? 'esg-analytics-filters-sidebar' : 'd-flex align-items-center flex-wrap'}`}>
        {/* In view mode, show entity tree, frequency (periodType), and reporting period */}
        {!editMode && (
          <>
            {renderFrequencyDisplay()}
            {renderReportingPeriodDisplay()}
            {renderEntityTreeView()}
            {renderApplyFiltersButton()}
          </>
        )}
        {/* In edit mode, show all filters */}
        {editMode && (
          <>
            {renderCategoryDropdown()}
            {renderTopicDropdown()}
            {renderParentEntityDropdown()}
            {renderChildEntityDropdown()}
            {renderPeriodTypeDropdown()}
            {renderReportingPeriodDropdown()}
            {renderApplyFiltersButton()}
          </>
        )}
      </div>

      {/* Only show config form in edit mode */}
      {editMode && renderConfigForm()}
      {editMode && renderDeleteConfigRowModal()}
      {editMode && renderDeleteLevel2RowModal()}
    </div>
  );
};

export default withTranslation()(ESGUnitWiseConfigForm);
